import { useState, useEffect, useCallback, useRef } from 'react';
import QRCode from 'qrcode';
import { PieChart, Pie, Cell, Tooltip, ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Legend } from 'recharts';
import '../Admin.css';
import { logo } from '../assets/media';

const API = '/api';

// ── Shared helpers ──
// Light-weight client-side validators — server re-validates everything too.
const validEmail = (v) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test((v || '').trim());
const validAmount = (v) => !isNaN(parseFloat(v)) && parseFloat(v) > 0;

// ── Shared selection hook ──
// Manages a Set of selected IDs for any list of items.
// Returned helpers keep the selection in sync when the item list changes.
function useSelection(items) {
  const [selected, setSelected] = useState(new Set());
  const ids = items.map(i => i.id);
  const allSelected = ids.length > 0 && ids.every(id => selected.has(id));
  const toggle    = (id) => setSelected(s => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const toggleAll = () => setSelected(allSelected ? new Set() : new Set(ids));
  const clear     = () => setSelected(new Set());
  const remove    = (ids) => setSelected(s => { const n = new Set(s); ids.forEach(id => n.delete(id)); return n; });
  return { selected, toggle, toggleAll, allSelected, clear, remove };
}

// ── Login Screen ──
function Login({ onLogin }) {
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      const res = await fetch(`${API}/admin/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ password }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Login failed');
      onLogin(data.token);
    } catch (err) {
      if (err.message === 'Failed to fetch') {
        setError('Cannot reach server. Make sure the backend is running on port 3001.');
      } else {
        setError(err.message);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="adm-root">
      <div className="adm-login">
        <form className="adm-login-box" onSubmit={submit}>
          <img src={logo} alt="7 Elemente" className="adm-login-logo" />
          <h1 className="adm-login-title">Admin Panel</h1>
          <p className="adm-login-sub">Enter your admin password to continue</p>
          {error && <div className="adm-error">{error}</div>}
          <input
            className="adm-input"
            type="password"
            placeholder="Password"
            value={password}
            onChange={e => setPassword(e.target.value)}
            autoFocus
          />
          <button className="adm-btn" type="submit" disabled={loading || !password}>
            {loading ? <span className="adm-spinner" /> : 'Sign In'}
          </button>
        </form>
      </div>
    </div>
  );
}

// ── Dashboard View ──
function Dashboard({ token, onNavigate }) {
  const [stats, setStats] = useState(null);
  const [recent, setRecent] = useState([]);
  const [sowTotal, setSowTotal] = useState(null);

  useEffect(() => {
    const headers = { 'x-admin-token': token };
    Promise.all([
      fetch(`${API}/admin/stats`, { headers }).then(r => r.json()),
      fetch(`${API}/admin/contacts`, { headers }).then(r => r.json()),
      fetch(`${API}/admin/sows`, { headers }).then(r => r.json()),
    ]).then(([s, contacts, sows]) => {
      setStats(s);
      setRecent(contacts.slice(0, 5));
      if (Array.isArray(sows)) {
        const total = sows.reduce((sum, s) => sum + parseFloat(s.cost_of_project || 0), 0);
        setSowTotal(total);
      }
    });
  }, [token]);

  return (
    <div className="adm-dashboard">
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 16 }}>
        <div>
          <h2 className="adm-page-title">Dashboard</h2>
          <p className="adm-page-sub">Overview of your contact submissions</p>
        </div>
        <div style={{ display: 'flex', gap: 10 }}>
          {stats && stats.unread > 0 && (
            <button className="adm-notif-bell" onClick={() => onNavigate('inbox')} title="Unread messages">
              <span className="adm-notif-icon">🔔</span>
              <span className="adm-notif-badge">{stats.unread}</span>
            </button>
          )}
          {stats && stats.pendingReceipts > 0 && (
            <button className="adm-notif-bell" onClick={() => onNavigate('payments')} title="Signed receipts awaiting review" style={{ background: 'rgba(110,252,245,0.08)', borderColor: 'rgba(110,252,245,0.25)' }}>
              <span className="adm-notif-icon">💳</span>
              <span className="adm-notif-badge" style={{ background: '#6efcf5', color: '#000' }}>{stats.pendingReceipts}</span>
            </button>
          )}
        </div>
      </div>

      <div className="adm-stats">
        <div className="adm-stat-card">
          <div className="adm-stat-label">Total</div>
          <div className="adm-stat-value">{stats ? stats.total : '—'}</div>
        </div>
        <div className="adm-stat-card">
          <div className="adm-stat-label">Unread</div>
          <div className="adm-stat-value">{stats ? stats.unread : '—'}</div>
        </div>
        <div className="adm-stat-card">
          <div className="adm-stat-label">Today</div>
          <div className="adm-stat-value">{stats ? stats.today : '—'}</div>
        </div>
        <div className="adm-stat-card" style={{ cursor: 'pointer' }} onClick={() => onNavigate('contracts_msa')}>
          <div className="adm-stat-label">MSAs</div>
          <div className="adm-stat-value" style={{ color: '#a78bfa' }}>{stats ? stats.totalMSAs : '—'}</div>
        </div>
        <div className="adm-stat-card" style={{ cursor: 'pointer' }} onClick={() => onNavigate('contracts_sow')}>
          <div className="adm-stat-label">SOWs</div>
          <div className="adm-stat-value" style={{ color: '#fc0071' }}>{stats ? stats.totalSOWs : '—'}</div>
        </div>
        {stats && stats.pendingReceipts > 0 && (
          <div className="adm-stat-card" style={{ borderColor: 'rgba(110,252,245,0.2)', cursor: 'pointer' }} onClick={() => onNavigate('payments')}>
            <div className="adm-stat-label">Receipts to Review</div>
            <div className="adm-stat-value" style={{ color: '#6efcf5' }}>{stats.pendingReceipts}</div>
          </div>
        )}
      </div>

      {stats && stats.tiers && (() => {
        const fmt = n => Number(n||0).toLocaleString('de-DE', { minimumFractionDigits: 0, maximumFractionDigits: 0 });
        const TIER_DEFS = [
          { key: 'tier1',   label: 'Tier 1',  color: '#6efcf5', targetUnits: 12, targetRev: 30000 },
          { key: 'tier2',   label: 'Tier 2',  color: '#a78bfa', targetUnits: 4,  targetRev: 30000 },
          { key: 'tier3',   label: 'Tier 3',  color: '#f59e0b', targetUnits: 1,  targetRev: 22000 },
          { key: 'hosting', label: 'Hosting', color: '#fc0071', targetUnits: 12, targetRev: 3000  },
        ];

        const tierData = [
          ...TIER_DEFS.map(t => {
            const d = t.key === 'hosting' ? stats.hosting : (stats.tiers[t.key] || { count: 0, revenue: 0 });
            return { ...t, count: d.count || 0, revenue: d.revenue || 0 };
          }),
          ...(stats.tiers.untiered?.count > 0 ? [{ key:'untiered', label:'Other', color:'#888', targetUnits:0, targetRev:0, count: stats.tiers.untiered.count, revenue: stats.tiers.untiered.revenue || 0 }] : []),
        ];

        const pieData = tierData.map(t => ({ name: t.label, value: t.revenue || 0.001, color: t.color }));
        const barData = TIER_DEFS.map(t => {
          const d = tierData.find(x => x.key === t.key);
          return { name: t.label, Achieved: d.count, Target: t.targetUnits - d.count > 0 ? t.targetUnits - d.count : 0 };
        });

        const totalRev       = sowTotal != null ? sowTotal : (stats.totalRevenue != null ? stats.totalRevenue : tierData.reduce((s, t) => s + t.revenue, 0));
        const totalGoalRev   = TIER_DEFS.reduce((s, t) => s + t.targetRev, 0);
        const totalUnits     = stats.totalSOWs || 0;
        const totalGoalUnits = TIER_DEFS.reduce((s, t) => s + t.targetUnits, 0);
        const overallRevPct  = Math.min(100, Math.round((totalRev / totalGoalRev) * 100));
        const overallUnitPct = Math.min(100, Math.round((totalUnits / totalGoalUnits) * 100));

        return (
          <div className="dash-charts-section">
            <div className="adm-section-header" style={{ marginBottom: 16 }}>
              <span className="adm-section-title">Revenue & Project Goals</span>
              <button className="adm-btn adm-btn-ghost adm-btn-sm" onClick={() => onNavigate('contracts_sow')}>View SOWs →</button>
            </div>

            {/* 2026 Annual Goal Banner */}
            <div className="dash-goal-banner">
              <div className="dash-goal-banner-year">2026 Annual Goal</div>
              <div className="dash-goal-banner-cols">
                <div className="dash-goal-banner-item">
                  <div className="dash-goal-banner-label">Revenue</div>
                  <div className="dash-goal-banner-nums">
                    <span className="dash-goal-banner-achieved">€{fmt(totalRev)}</span>
                    <span className="dash-goal-banner-sep">/</span>
                    <span className="dash-goal-banner-target">€{fmt(totalGoalRev)}</span>
                  </div>
                  <div className="dash-goal-banner-track">
                    <div className="dash-goal-banner-fill dash-goal-banner-fill--rev" style={{ width: `${overallRevPct}%` }} />
                  </div>
                  <div className="dash-goal-banner-pct" style={{ color: overallRevPct >= 100 ? '#3bdfb5' : '#fc0071' }}>{overallRevPct}%</div>
                </div>
                <div className="dash-goal-banner-divider" />
                <div className="dash-goal-banner-item">
                  <div className="dash-goal-banner-label">Projects</div>
                  <div className="dash-goal-banner-nums">
                    <span className="dash-goal-banner-achieved">{totalUnits}</span>
                    <span className="dash-goal-banner-sep">/</span>
                    <span className="dash-goal-banner-target">{totalGoalUnits}</span>
                  </div>
                  <div className="dash-goal-banner-track">
                    <div className="dash-goal-banner-fill dash-goal-banner-fill--unit" style={{ width: `${overallUnitPct}%` }} />
                  </div>
                  <div className="dash-goal-banner-pct" style={{ color: overallUnitPct >= 100 ? '#3bdfb5' : '#6efcf5' }}>{overallUnitPct}%</div>
                </div>
              </div>
            </div>

            <div className="dash-charts-grid">
              {/* Donut chart — revenue distribution */}
              <div className="dash-chart-card">
                <div className="dash-chart-title">Revenue by Tier</div>
                <div className="dash-chart-sub">Total: €{fmt(totalRev)}</div>
                <ResponsiveContainer width="100%" height={220}>
                  <PieChart>
                    <Pie data={pieData} cx="50%" cy="50%" innerRadius={60} outerRadius={90} paddingAngle={3} dataKey="value">
                      {pieData.map((entry, i) => <Cell key={i} fill={entry.color} />)}
                    </Pie>
                    <Tooltip formatter={(v, name) => [`€${fmt(v)}`, name]} contentStyle={{ background: '#0f172a', border: '1px solid #1e293b', borderRadius: 8, fontSize: 12 }} />
                  </PieChart>
                </ResponsiveContainer>
                <div className="dash-pie-legend">
                  {tierData.map(t => (
                    <div key={t.key} className="dash-pie-legend-item">
                      <span className="dash-pie-dot" style={{ background: t.color }} />
                      <span>{t.label}</span>
                      <span style={{ color: t.color, fontWeight: 700 }}>€{fmt(t.revenue)}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Bar chart — projects achieved vs target */}
              <div className="dash-chart-card">
                <div className="dash-chart-title">Projects Progress</div>
                <div className="dash-chart-sub">Achieved vs remaining target</div>
                <ResponsiveContainer width="100%" height={220}>
                  <BarChart data={barData} barSize={28} margin={{ top: 8, right: 8, bottom: 0, left: -20 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" />
                    <XAxis dataKey="name" tick={{ fill: '#94a3b8', fontSize: 12 }} axisLine={false} tickLine={false} />
                    <YAxis tick={{ fill: '#94a3b8', fontSize: 11 }} axisLine={false} tickLine={false} allowDecimals={false} />
                    <Tooltip contentStyle={{ background: '#0f172a', border: '1px solid #1e293b', borderRadius: 8, fontSize: 12 }} />
                    <Legend wrapperStyle={{ fontSize: 12, color: '#94a3b8' }} />
                    <Bar dataKey="Achieved" stackId="a" fill="#6efcf5" radius={[0,0,0,0]} />
                    <Bar dataKey="Target"   stackId="a" fill="rgba(255,255,255,0.08)" radius={[4,4,0,0]} />
                  </BarChart>
                </ResponsiveContainer>

                <div className="dash-tier-rows">
                  {tierData.map(t => {
                    const pct = t.targetUnits > 0 ? Math.min(100, Math.round((t.count / t.targetUnits) * 100)) : 0;
                    return (
                      <div key={t.key} className="dash-tier-row">
                        <span className="dash-tier-label" style={{ color: t.color }}>{t.label}</span>
                        <span className="dash-tier-count">{t.count}/{t.targetUnits}</span>
                        <div className="dash-tier-bar"><div style={{ width: `${pct}%`, height: '100%', background: t.color, borderRadius: 99 }} /></div>
                        <span className="dash-tier-pct" style={{ color: t.color }}>{pct}%</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>

            {/* Goal Completion — full width */}
            <div className="dash-chart-card" style={{ marginTop: 16 }}>
              <div className="dash-chart-title">Goal Completion</div>
              <div className="dash-chart-sub">Units &amp; revenue progress towards each tier target</div>
              <div className="dash-goal-grid">
                {tierData.map(t => {
                  const unitPct = t.targetUnits > 0 ? Math.min(100, Math.round((t.count / t.targetUnits) * 100)) : 0;
                  const revPct  = t.targetRev  > 0 ? Math.min(100, Math.round((t.revenue / t.targetRev)  * 100)) : 0;
                  const done = unitPct >= 100 && revPct >= 100;
                  return (
                    <div key={t.key} className={`dash-goal-tile${done ? ' dash-goal-tile--done' : ''}`}>
                      <div className="dash-goal-tile-header">
                        <span className="dash-goal-tile-name" style={{ color: t.color }}>{t.label}</span>
                        {done && <span className="dash-goal-badge">✓ Goal Reached</span>}
                      </div>
                      <div className="dash-goal-bar-row">
                        <span className="dash-goal-bar-type">Units</span>
                        <div className="dash-goal-bar-track">
                          <div className="dash-goal-bar-fill" style={{ width: `${unitPct}%`, background: t.color }} />
                        </div>
                        <span className="dash-goal-bar-nums">{t.count}/{t.targetUnits}</span>
                        <span className="dash-goal-bar-pct" style={{ color: unitPct >= 100 ? '#3bdfb5' : t.color }}>{unitPct}%</span>
                      </div>
                      <div className="dash-goal-bar-row">
                        <span className="dash-goal-bar-type">Revenue</span>
                        <div className="dash-goal-bar-track">
                          <div className="dash-goal-bar-fill" style={{ width: `${revPct}%`, background: t.color, opacity: 0.65 }} />
                        </div>
                        <span className="dash-goal-bar-nums">€{fmt(t.revenue)}/€{fmt(t.targetRev)}</span>
                        <span className="dash-goal-bar-pct" style={{ color: revPct >= 100 ? '#3bdfb5' : t.color }}>{revPct}%</span>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        );
      })()}

      <div className="adm-section-header">
        <span className="adm-section-title">Recent Messages</span>
        {recent.length > 0 && (
          <button className="adm-btn adm-btn-ghost adm-btn-sm" onClick={() => onNavigate('inbox')}>
            View all →
          </button>
        )}
      </div>

      <div className="adm-msg-list">
        {recent.length === 0 && <div className="adm-empty">No messages yet.</div>}
        {recent.map(msg => (
          <div key={msg.id} className={`adm-msg-card${msg.is_read ? '' : ' unread'}`}>
            <div className="adm-msg-top">
              <span className="adm-msg-name">{msg.name}</span>
              <span className="adm-msg-email">{msg.email}</span>
              {msg.company && <span className="adm-msg-company">{msg.company}</span>}
              <span className="adm-msg-date" style={{ marginLeft: 'auto' }}>
                {new Date(msg.created_at).toLocaleDateString()}
              </span>
              <span className={msg.is_read ? 'adm-badge-read' : 'adm-badge-new'}>
                {msg.is_read ? 'READ' : 'NEW'}
              </span>
            </div>
            <div className="adm-msg-preview">{msg.message}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Inbox View ──
function Inbox({ token }) {
  const [contacts, setContacts] = useState([]);
  const [expanded, setExpanded] = useState(null);
  const [confirm, setConfirm] = useState(null);
  const [confirmBulkDel, setConfirmBulkDel] = useState(false);
  const [filter, setFilter] = useState('all');

  const load = useCallback(() => {
    fetch(`${API}/admin/contacts`, { headers: { 'x-admin-token': token } })
      .then(r => r.json())
      .then(setContacts);
  }, [token]);

  useEffect(() => { load(); }, [load]);

  const markRead = async (id) => {
    await fetch(`${API}/admin/contacts/${id}/read`, { method: 'PATCH', headers: { 'x-admin-token': token } });
    setContacts(cs => cs.map(c => c.id === id ? { ...c, is_read: 1 } : c));
  };

  const doDelete = async (id) => {
    await fetch(`${API}/admin/contacts/${id}`, { method: 'DELETE', headers: { 'x-admin-token': token } });
    setContacts(cs => cs.filter(c => c.id !== id));
    setConfirm(null);
    if (expanded === id) setExpanded(null);
  };

  const filtered = contacts.filter(c => {
    if (filter === 'unread') return !c.is_read;
    if (filter === 'read') return c.is_read;
    return true;
  });

  const sel = useSelection(filtered);

  const bulkMarkRead = async () => {
    await Promise.all([...sel.selected].map(id => fetch(`${API}/admin/contacts/${id}/read`, { method: 'PATCH', headers: { 'x-admin-token': token } })));
    setContacts(cs => cs.map(c => sel.selected.has(c.id) ? { ...c, is_read: 1 } : c));
    sel.clear();
  };

  const bulkDelete = async () => {
    await Promise.all([...sel.selected].map(id => fetch(`${API}/admin/contacts/${id}`, { method: 'DELETE', headers: { 'x-admin-token': token } })));
    setContacts(cs => cs.filter(c => !sel.selected.has(c.id)));
    sel.clear();
  };

  return (
    <div>
      <h2 className="adm-page-title">Inbox</h2>
      <p className="adm-page-sub">{contacts.length} message{contacts.length !== 1 ? 's' : ''} total</p>

      <div className="adm-inbox-toolbar">
        <input type="checkbox" className="adm-checkbox" checked={sel.allSelected} onChange={sel.toggleAll} title="Select all" />
        {['all', 'unread', 'read'].map(f => (
          <button key={f} className={`adm-btn adm-btn-sm ${filter === f ? '' : 'adm-btn-ghost'}`}
            onClick={() => { setFilter(f); sel.clear(); }} style={{ textTransform: 'capitalize' }}>{f}</button>
        ))}
      </div>

      {sel.selected.size > 0 && (
        <div className="adm-bulk-bar">
          <span className="adm-bulk-count">{sel.selected.size} selected</span>
          <button className="adm-btn adm-btn-ghost adm-btn-sm" onClick={sel.clear}>Clear</button>
          <button className="adm-btn adm-btn-ghost adm-btn-sm" onClick={bulkMarkRead}>✓ Mark Read</button>
          <button className="adm-btn adm-btn-danger adm-btn-sm" onClick={() => setConfirmBulkDel(true)}>🗑 Delete ({sel.selected.size})</button>
        </div>
      )}

      {confirmBulkDel && (
        <div className="adm-confirm-overlay">
          <div className="adm-confirm-box">
            <p className="adm-confirm-title">Delete {sel.selected.size} message{sel.selected.size !== 1 ? 's' : ''}?</p>
            <p className="adm-confirm-text">This action cannot be undone.</p>
            <div className="adm-confirm-actions">
              <button className="adm-btn adm-btn-ghost adm-btn-sm" onClick={() => setConfirmBulkDel(false)}>Cancel</button>
              <button className="adm-btn adm-btn-danger adm-btn-sm" onClick={() => { setConfirmBulkDel(false); bulkDelete(); }}>Delete</button>
            </div>
          </div>
        </div>
      )}

      <div className="adm-msg-list">
        {filtered.length === 0 && <div className="adm-empty">No messages.</div>}
        {filtered.map(msg => (
          <div key={msg.id} className={`adm-msg-card${msg.is_read ? '' : ' unread'}${sel.selected.has(msg.id) ? ' adm-row-selected' : ''}`}>
            <div className="adm-msg-top">
              <input type="checkbox" className="adm-checkbox" checked={sel.selected.has(msg.id)} onChange={() => sel.toggle(msg.id)} onClick={e => e.stopPropagation()} />
              <span className="adm-msg-name">{msg.name}</span>
              <span className="adm-msg-email">{msg.email}</span>
              {msg.company && <span className="adm-msg-company">{msg.company}</span>}
              <span className="adm-msg-date" style={{ marginLeft: 'auto' }}>
                {new Date(msg.created_at).toLocaleString()}
              </span>
              <span className={msg.is_read ? 'adm-badge-read' : 'adm-badge-new'}>
                {msg.is_read ? 'READ' : 'NEW'}
              </span>
            </div>

            {expanded === msg.id ? (
              <div className="adm-msg-body">{msg.message}</div>
            ) : (
              <div className="adm-msg-preview">{msg.message}</div>
            )}

            <div className="adm-msg-actions">
              <button
                className="adm-btn adm-btn-ghost adm-btn-sm"
                onClick={() => {
                  setExpanded(expanded === msg.id ? null : msg.id);
                  if (!msg.is_read) markRead(msg.id);
                }}
              >
                {expanded === msg.id ? 'Collapse' : 'Read'}
              </button>
              {!msg.is_read && (
                <button className="adm-btn adm-btn-ghost adm-btn-sm" onClick={() => markRead(msg.id)}>
                  Mark read
                </button>
              )}
              <button
                className="adm-btn adm-btn-danger adm-btn-sm"
                onClick={() => setConfirm(msg.id)}
              >
                Delete
              </button>
            </div>
          </div>
        ))}
      </div>

      {confirm && (
        <div className="adm-confirm-overlay">
          <div className="adm-confirm-box">
            <p className="adm-confirm-title">Delete message?</p>
            <p className="adm-confirm-text">This action cannot be undone.</p>
            <div className="adm-confirm-actions">
              <button className="adm-btn adm-btn-ghost adm-btn-sm" onClick={() => setConfirm(null)}>
                Cancel
              </button>
              <button className="adm-btn adm-btn-danger adm-btn-sm" onClick={() => doDelete(confirm)}>
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ── YouTube helpers ──
function fmtDuration(secs) {
  if (!secs) return '';
  const s = parseInt(secs, 10);
  const m = Math.floor(s / 60);
  const h = Math.floor(m / 60);
  if (h > 0) return `${h}:${String(m % 60).padStart(2,'0')}:${String(s % 60).padStart(2,'0')}`;
  return `${m}:${String(s % 60).padStart(2,'0')}`;
}

async function ytDownload(token, videoUrl, format) {
  try {
    const res = await fetch(
      `${API}/admin/youtube/download?url=${encodeURIComponent(videoUrl)}&format=${format}`,
      { headers: { 'x-admin-token': token } }
    );
    if (!res.ok) { const e = await res.json().catch(() => ({ error: 'Download failed' })); throw new Error(e.error || 'Download failed'); }
    const blob = await res.blob();
    const blobUrl = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = blobUrl;
    a.download = format === 'mp3' ? 'audio.mp3' : 'video.mp4';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    setTimeout(() => URL.revokeObjectURL(blobUrl), 10000);
  } catch (e) {
    alert('Download failed: ' + e.message);
  }
}

// ── YouTube: One Link ──
function YouTubeSingle({ token }) {
  const [url, setUrl] = useState('');
  const [info, setInfo] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const getInfo = async () => {
    if (!url.trim()) return;
    setLoading(true); setError(''); setInfo(null);
    try {
      const res = await fetch(`${API}/admin/youtube/info?url=${encodeURIComponent(url)}`, { headers: { 'x-admin-token': token } });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to fetch info');
      if (data.type !== 'video') throw new Error('This looks like a playlist — use the Series tab instead.');
      setInfo(data);
    } catch (e) { setError(e.message); }
    finally { setLoading(false); }
  };

  return (
    <div>
      <h2 className="adm-page-title">YouTube — One Link</h2>
      <p className="adm-page-sub">Download a single video to your computer as MP4 or MP3</p>
      <div className="yt-downloader">
        <div className="yt-input-row">
          <input className="adm-input yt-url-input" placeholder="Paste a YouTube video URL…" value={url}
            onChange={e => setUrl(e.target.value)} onKeyDown={e => e.key === 'Enter' && getInfo()} />
          <button className="adm-btn adm-btn-sm" onClick={getInfo} disabled={loading}>
            {loading ? 'Loading…' : 'Get Info'}
          </button>
        </div>
        {error && <p className="yt-error">{error}</p>}
        {info && (
          <div className="yt-result">
            {info.thumbnail && <img className="yt-thumb" src={info.thumbnail} alt="" />}
            <div className="yt-meta">
              <p className="yt-title">{info.title}</p>
              <p className="yt-sub">{info.author} · {fmtDuration(info.duration)}</p>
              <div className="yt-btns">
                <button className="adm-btn adm-btn-sm" onClick={() => ytDownload(token, info.url, 'mp4')}>⬇ MP4</button>
                <button className="adm-btn adm-btn-ghost adm-btn-sm" onClick={() => ytDownload(token, info.url, 'mp3')}>⬇ MP3</button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ── YouTube: Series / Playlist ──
function YouTubeSeries({ token }) {
  const [url, setUrl] = useState('');
  const [info, setInfo] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [selected, setSelected] = useState(new Set());
  const [format, setFormat] = useState('mp4');

  const getInfo = async () => {
    if (!url.trim()) return;
    setLoading(true); setError(''); setInfo(null); setSelected(new Set());
    try {
      const res = await fetch(`${API}/admin/youtube/info?url=${encodeURIComponent(url)}`, { headers: { 'x-admin-token': token } });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to fetch info');
      if (data.type !== 'playlist') throw new Error('This looks like a single video — use the One Link tab instead.');
      setInfo(data);
    } catch (e) { setError(e.message); }
    finally { setLoading(false); }
  };

  const toggleOne = (id) => setSelected(s => {
    const n = new Set(s);
    n.has(id) ? n.delete(id) : n.add(id);
    return n;
  });

  const allIds = info ? info.items.map(i => i.id) : [];
  const allSelected = allIds.length > 0 && allIds.every(id => selected.has(id));

  const toggleAll = () => setSelected(allSelected ? new Set() : new Set(allIds));

  const downloadSelected = () => {
    const items = info.items.filter(i => selected.has(i.id));
    items.forEach((item, idx) => {
      setTimeout(() => ytDownload(token, item.url, format), idx * 800);
    });
  };

  return (
    <div>
      <h2 className="adm-page-title">YouTube — Series</h2>
      <p className="adm-page-sub">Download all videos from a playlist to your computer</p>
      <div className="yt-downloader">
        <div className="yt-input-row">
          <input className="adm-input yt-url-input" placeholder="Paste a YouTube playlist URL…" value={url}
            onChange={e => setUrl(e.target.value)} onKeyDown={e => e.key === 'Enter' && getInfo()} />
          <button className="adm-btn adm-btn-sm" onClick={getInfo} disabled={loading}>
            {loading ? 'Loading…' : 'Get Info'}
          </button>
        </div>
        {error && <p className="yt-error">{error}</p>}
        {info && (
          <div className="yt-playlist">
            <div className="yt-playlist-header">
              <p className="yt-playlist-title">{info.title} <span className="yt-playlist-count">{info.count} videos</span></p>
              <div className="yt-bulk-bar">
                <label className="yt-select-all">
                  <input type="checkbox" checked={allSelected} onChange={toggleAll} />
                  Select All
                </label>
                <select className="adm-input" style={{width:'auto',padding:'4px 10px',fontSize:13}} value={format} onChange={e => setFormat(e.target.value)}>
                  <option value="mp4">MP4</option>
                  <option value="mp3">MP3</option>
                </select>
                <button className="adm-btn adm-btn-sm" disabled={selected.size === 0} onClick={downloadSelected}>
                  ⬇ Download Selected ({selected.size})
                </button>
              </div>
            </div>
            <div className="yt-playlist-list">
              {info.items.map((item, i) => (
                <div key={item.id} className={`yt-playlist-item${selected.has(item.id) ? ' yt-selected' : ''}`}>
                  <input type="checkbox" className="adm-checkbox" checked={selected.has(item.id)} onChange={() => toggleOne(item.id)} />
                  <span className="yt-playlist-num">{i + 1}</span>
                  {item.thumbnail && <img className="yt-playlist-thumb" src={item.thumbnail} alt="" />}
                  <div className="yt-playlist-meta">
                    <p className="yt-playlist-item-title">{item.title}</p>
                    <p className="yt-sub">{item.duration}</p>
                  </div>
                  <div className="yt-btns">
                    <button className="adm-btn adm-btn-sm" style={{fontSize:11}} onClick={() => ytDownload(token, item.url, 'mp4')}>MP4</button>
                    <button className="adm-btn adm-btn-ghost adm-btn-sm" style={{fontSize:11}} onClick={() => ytDownload(token, item.url, 'mp3')}>MP3</button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ── Clock Hours ──
const today = () => new Date().toISOString().split('T')[0];

function ClockHours({ token }) {
  const [form, setForm] = useState({ companyName: '', clientName: '', clientEmail: '', date: today(), startTime: '', endTime: '', hourlyRate: '', notes: '' });
  const [draftLang, setDraftLang] = useState('en');
  const [status, setStatus] = useState('idle');
  const [error, setError] = useState('');
  const [clients, setClients] = useState([]);

  useEffect(() => {
    fetch(`${API}/admin/clients`, { headers: { 'x-admin-token': token } })
      .then(r => r.json()).then(d => setClients(Array.isArray(d) ? d : [])).catch(() => {});
  }, [token]);

  const companies = [...new Map(clients.filter(c => c.company).map(c => [c.company, c])).values()].sort((a, b) => a.company.localeCompare(b.company));
  const contactsForCompany = form.companyName ? clients.filter(c => c.company === form.companyName) : clients;

  const totalHours = () => {
    if (!form.startTime || !form.endTime) return null;
    const [sh, sm] = form.startTime.split(':').map(Number);
    const [eh, em] = form.endTime.split(':').map(Number);
    const diff = (eh * 60 + em) - (sh * 60 + sm);
    if (diff <= 0) return null;
    const h = Math.floor(diff / 60);
    const m = diff % 60;
    return m === 0 ? `${h}h` : `${h}h ${m}m`;
  };

  const draftTemplates = {
    en: (name, date, hours) =>
`Dear ${name},

Could you please confirm my hours by clicking the below link.

Date: ${date}
Hours: ${hours}

Thank you for giving me the opportunity to work with you.

Good Day!

Warmest regards,
Elizabeth`,
    de: (name, date, hours) =>
`Sehr geehrte/r ${name},

könnten Sie bitte meine Stunden durch Klicken auf den unten stehenden Link bestätigen.

Datum: ${date}
Stunden: ${hours}

Vielen Dank, dass Sie mir die Möglichkeit gegeben haben, mit Ihnen zu arbeiten.

Einen schönen Tag noch!

Mit freundlichen Grüßen,
Elizabeth`,
    it: (name, date, hours) =>
`Gentile ${name},

Potrebbe confermare le mie ore cliccando sul link qui sotto.

Data: ${date}
Ore: ${hours}

Grazie per avermi dato l'opportunità di lavorare con lei.

Buona giornata!

Cordiali saluti,
Elizabeth`,
    fr: (name, date, hours) =>
`Cher/Chère ${name},

Pourriez-vous confirmer mes heures en cliquant sur le lien ci-dessous.

Date : ${date}
Heures : ${hours}

Merci de m'avoir donné l'opportunité de travailler avec vous.

Bonne journée !

Cordialement,
Elizabeth`,
  };

  const fillDraft = (lang) => {
    const l = lang || draftLang;
    const name = form.clientName.trim() || '[Client Name]';
    const date = form.date ? new Date(form.date).toLocaleDateString('en-GB', { day: '2-digit', month: 'long', year: 'numeric' }) : '[Date]';
    const hours = totalHours() || '[Hours]';
    setForm(f => ({ ...f, notes: draftTemplates[l](name, date, hours) }));
  };

  const handleChange = e => setForm(f => ({ ...f, [e.target.name]: e.target.value }));

  const handleSubmit = async () => {
    setError('');
    if (!form.clientName.trim() || !form.clientEmail.trim() || !form.date || !form.startTime || !form.endTime)
      return setError('Please fill in all required fields.');
    if (!totalHours()) return setError('End time must be after start time.');
    setStatus('sending');
    try {
      const res = await fetch(`${API}/admin/clock-hours`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'x-admin-token': token },
        body: JSON.stringify({ ...form, totalHours: totalHours() }),
      });
      if (res.ok) {
        setStatus('sent');
        setForm({ companyName: '', clientName: '', clientEmail: '', date: today(), startTime: '', endTime: '', hourlyRate: '', notes: '' });
      } else {
        const e = await res.json().catch(() => ({}));
        setError(e.error || 'Failed to send. Please try again.');
        setStatus('idle');
      }
    } catch {
      setError('Network error. Please try again.');
      setStatus('idle');
    }
  };

  return (
    <div className="adm-section">
      <h2 className="adm-page-title">Clock Hours</h2>
      {status === 'sent' ? (
        <div className="adm-success-box">
          <span style={{ fontSize: 32 }}>✓</span>
          <p>Hours sent to client successfully!</p>
          <button className="adm-btn" onClick={() => setStatus('idle')}>Log More Hours</button>
        </div>
      ) : (
        <div className="adm-card" style={{ maxWidth: 560 }}>
          <div className="adm-form-group">
            <label className="adm-label">Company <span style={{ color: '#888', fontSize: 11 }}>— optional</span></label>
            <select className="adm-input" value={form.companyName}
              onChange={e => setForm(f => ({ ...f, companyName: e.target.value, clientName: '', clientEmail: '' }))}>
              <option value="">— Select company (optional) —</option>
              {companies.map(c => <option key={c.id} value={c.company}>{c.company}</option>)}
            </select>
          </div>
          <div className="adm-form-row">
            <div className="adm-form-group">
              <label className="adm-label">Contact Name *</label>
              {contactsForCompany.length > 0 ? (
                <select className="adm-input" value={form.clientName}
                  onChange={e => {
                    const sel = contactsForCompany.find(c => c.name === e.target.value);
                    if (sel) setForm(f => ({ ...f, clientName: sel.name, clientEmail: sel.email || '' }));
                    else setForm(f => ({ ...f, clientName: e.target.value }));
                  }}>
                  <option value="">— Select contact —</option>
                  {contactsForCompany.map(c => <option key={c.id} value={c.name}>{c.name}</option>)}
                </select>
              ) : (
                <input className="adm-input" name="clientName" value={form.clientName} onChange={handleChange} placeholder="e.g. Barbara Müller" />
              )}
            </div>
            <div className="adm-form-group">
              <label className="adm-label">Contact Email *</label>
              <input className="adm-input" type="email" name="clientEmail" value={form.clientEmail} onChange={handleChange} placeholder="contact@company.com" />
            </div>
          </div>
          <div className="adm-form-group">
            <label className="adm-label">Date *</label>
            <input className="adm-input" type="date" name="date" value={form.date} onChange={handleChange} />
          </div>
          <div className="adm-form-row">
            <div className="adm-form-group">
              <label className="adm-label">Start Time *</label>
              <input className="adm-input" type="time" name="startTime" value={form.startTime} onChange={handleChange} />
            </div>
            <div className="adm-form-group">
              <label className="adm-label">End Time *</label>
              <input className="adm-input" type="time" name="endTime" value={form.endTime} onChange={handleChange} />
            </div>
          </div>
          <div className="adm-form-group">
            <label className="adm-label">Hourly Rate (€) <span style={{ color: '#888', fontSize: 11 }}>— internal only</span></label>
            <input className="adm-input" type="number" name="hourlyRate" value={form.hourlyRate} onChange={handleChange} placeholder="e.g. 85" min="0" step="0.01" />
          </div>
          {totalHours() && (
            <div className="adm-hours-badge" style={{ display: 'flex', gap: 20 }}>
              <span>Total: <strong>{totalHours()}</strong></span>
              {form.hourlyRate && (() => {
                const [h, m] = [parseInt(totalHours()), totalHours().includes('m') ? parseInt(totalHours().split('h')[1]) : 0];
                const total = ((h || 0) + (m || 0) / 60) * parseFloat(form.hourlyRate);
                return <span>Amount: <strong>€{total.toFixed(2)}</strong></span>;
              })()}
            </div>
          )}
          <div className="adm-form-group">
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
              <label className="adm-label" style={{ margin: 0 }}>Notes</label>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                {['en', 'de', 'it', 'fr'].map(l => (
                  <button key={l} type="button"
                    onClick={() => { setDraftLang(l); if (form.notes) fillDraft(l); }}
                    style={{
                      padding: '3px 9px', borderRadius: 6, fontSize: 12, cursor: 'pointer', border: '1px solid',
                      background: draftLang === l ? 'rgba(110,66,245,0.3)' : 'transparent',
                      borderColor: draftLang === l ? '#6e42f5' : 'rgba(255,255,255,0.15)',
                      color: draftLang === l ? '#fff' : '#aaa',
                    }}
                  >{l.toUpperCase()}</button>
                ))}
                <button className="adm-btn adm-btn-sm" type="button" onClick={() => fillDraft()}>✦ Fill Draft</button>
              </div>
            </div>
            <textarea className="adm-input" name="notes" value={form.notes} onChange={handleChange} placeholder="Work description, tasks completed..." rows={3} />
          </div>
          {error && <p style={{ color: '#fc0071', fontSize: 13, marginBottom: 8 }}>{error}</p>}
          <button className="adm-btn" onClick={handleSubmit} disabled={status === 'sending'}>
            {status === 'sending' ? 'Sending…' : '✉ Submit & Email Client'}
          </button>
        </div>
      )}
    </div>
  );
}

// ── Hours Log ──
function calcAmount(totalHours, rate) {
  if (!rate) return null;
  const hMatch = totalHours.match(/(\d+)h/);
  const mMatch = totalHours.match(/(\d+)m/);
  const h = hMatch ? parseInt(hMatch[1]) : 0;
  const m = mMatch ? parseInt(mMatch[1]) : 0;
  return ((h + m / 60) * parseFloat(rate)).toFixed(2);
}

function hoursToDecimal(str) {
  if (!str) return 0;
  const hMatch = str.match(/(\d+)h/);
  const mMatch = str.match(/(\d+)m/);
  return (hMatch ? parseInt(hMatch[1]) : 0) + (mMatch ? parseInt(mMatch[1]) : 0) / 60;
}

function decimalToHours(dec) {
  const h = Math.floor(dec);
  const m = Math.round((dec - h) * 60);
  return m === 0 ? `${h}h` : `${h}h ${m}m`;
}

function calcEditHours(startTime, endTime) {
  if (!startTime || !endTime) return null;
  const [sh, sm] = startTime.split(':').map(Number);
  const [eh, em] = endTime.split(':').map(Number);
  const diff = (eh * 60 + em) - (sh * 60 + sm);
  if (diff <= 0) return null;
  const h = Math.floor(diff / 60);
  const m = diff % 60;
  return m === 0 ? `${h}h` : `${h}h ${m}m`;
}

function HoursLog({ token }) {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [reminding, setReminding] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [msg, setMsg] = useState('');
  const [search, setSearch] = useState('');
  const [clientFilter, setClientFilter] = useState('');
  const [selected, setSelected] = useState(new Set());
  const [editEntry, setEditEntry] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [companyFilter, setCompanyFilter] = useState('');
  const [clients, setClients] = useState([]);

  const load = async () => {
    setLoading(true);
    const res = await fetch(`${API}/admin/hours-log`, { headers: { 'x-admin-token': token } });
    const data = await res.json().catch(() => []);
    setEntries(Array.isArray(data) ? data : []);
    setLoading(false);
  };

  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    fetch(`${API}/admin/clients`, { headers: { 'x-admin-token': token } })
      .then(r => r.json()).then(d => setClients(Array.isArray(d) ? d : [])).catch(() => {});
  }, [token]);

  const editCompanies = [...new Map(clients.filter(c => c.company).map(c => [c.company, c])).values()].sort((a, b) => a.company.localeCompare(b.company));
  const editContactsForCompany = editForm.companyName ? clients.filter(c => c.company === editForm.companyName) : clients;

  const sendReminder = async (id) => {
    setReminding(id); setMsg('');
    const res = await fetch(`${API}/admin/hours-log/${id}/remind`, { method: 'POST', headers: { 'x-admin-token': token } });
    if (res.ok) setMsg('Reminder sent!');
    else { const e = await res.json().catch(() => ({})); setMsg(e.error || 'Failed'); }
    setReminding(null);
    setTimeout(() => setMsg(''), 3000);
  };

  const deleteSelected = async () => {
    if (!selected.size) return;
    if (!window.confirm(`Delete ${selected.size} entr${selected.size === 1 ? 'y' : 'ies'}?`)) return;
    setDeleting(true);
    await Promise.all([...selected].map(id =>
      fetch(`${API}/admin/hours-log/${id}`, { method: 'DELETE', headers: { 'x-admin-token': token } })
    ));
    setSelected(new Set());
    await load();
    setDeleting(false);
    setMsg(`${selected.size > 1 ? 'Entries' : 'Entry'} deleted.`);
    setTimeout(() => setMsg(''), 3000);
  };

  const openEdit = (e, ev) => {
    ev.stopPropagation();
    setEditEntry(e);
    const d = new Date(e.work_date);
    const yyyy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const dd = String(d.getDate()).padStart(2, '0');
    setEditForm({
      date: `${yyyy}-${mm}-${dd}`,
      startTime: e.start_time ? e.start_time.slice(0, 5) : '',
      endTime: e.end_time ? e.end_time.slice(0, 5) : '',
      hourlyRate: e.hourly_rate ? String(e.hourly_rate) : '',
      notes: e.notes || '',
      companyName: e.company_name || '',
      clientName: e.client_name || '',
      clientEmail: e.client_email || '',
    });
  };

  const saveEdit = async () => {
    setSaving(true);
    const totalHours = calcEditHours(editForm.startTime, editForm.endTime) || editEntry.total_hours;
    const res = await fetch(`${API}/admin/hours-log/${editEntry.id}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json', 'x-admin-token': token },
      body: JSON.stringify({ ...editForm, totalHours }),
    });
    setSaving(false);
    if (res.ok) {
      setEditEntry(null);
      await load();
      setMsg('Entry updated.');
      setTimeout(() => setMsg(''), 3000);
    } else {
      const e = await res.json().catch(() => ({}));
      setMsg(e.error || 'Failed to save.');
    }
  };

  const filtered = entries.filter(e => {
    const matchSearch = !search ||
      e.client_name.toLowerCase().includes(search.toLowerCase()) ||
      e.client_email.toLowerCase().includes(search.toLowerCase()) ||
      (e.company_name || '').toLowerCase().includes(search.toLowerCase());
    const matchClient = !clientFilter || e.client_name === clientFilter;
    const matchCompany = !companyFilter || e.company_name === companyFilter;
    return matchSearch && matchClient && matchCompany;
  });

  const clientNames = [...new Set(entries.map(e => e.client_name))].sort();
  const companyNames = [...new Set(entries.map(e => e.company_name).filter(Boolean))].sort();

  const allChecked = filtered.length > 0 && filtered.every(e => selected.has(e.id));
  const toggleAll = () => {
    if (allChecked) setSelected(s => { const n = new Set(s); filtered.forEach(e => n.delete(e.id)); return n; });
    else setSelected(s => { const n = new Set(s); filtered.forEach(e => n.add(e.id)); return n; });
  };
  const toggleRow = (id) => setSelected(s => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });

  const selEntries = filtered.filter(e => selected.has(e.id));
  const totalSelHours = selEntries.reduce((acc, e) => acc + hoursToDecimal(e.total_hours), 0);
  const totalSelAmount = selEntries.reduce((acc, e) => {
    const a = calcAmount(e.total_hours, e.hourly_rate);
    return acc + (a ? parseFloat(a) : 0);
  }, 0);

  return (
    <div className="adm-section">
      <h2 className="adm-page-title">Hours Log</h2>
      {msg && <div style={{ marginBottom: 12, color: '#6efcf5', fontSize: 13 }}>{msg}</div>}

      {editEntry && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
          onClick={() => setEditEntry(null)}>
          <div style={{ background: '#1a1a2e', border: '1px solid rgba(110,66,245,0.3)', borderRadius: 14, padding: 28, width: '100%', maxWidth: 440, margin: '0 16px' }}
            onClick={ev => ev.stopPropagation()}>
            <h3 style={{ margin: '0 0 20px', fontSize: 18 }}>Edit Entry — {editEntry.client_name}</h3>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 }}>
              <div>
                <label className="adm-label">Company <span style={{ color: '#888', fontSize: 11 }}>— optional</span></label>
                <select className="adm-input" value={editForm.companyName}
                  onChange={e => setEditForm(f => ({ ...f, companyName: e.target.value, clientName: '', clientEmail: '' }))}
                  style={{ margin: 0 }}>
                  <option value="">— optional —</option>
                  {editCompanies.map(c => <option key={c.id} value={c.company}>{c.company}</option>)}
                </select>
              </div>
              <div>
                <label className="adm-label">Contact Name</label>
                {editContactsForCompany.length > 0 ? (
                  <select className="adm-input" value={editForm.clientName}
                    onChange={e => {
                      const sel = editContactsForCompany.find(c => c.name === e.target.value);
                      if (sel) setEditForm(f => ({ ...f, clientName: sel.name, clientEmail: sel.email || '' }));
                      else setEditForm(f => ({ ...f, clientName: e.target.value }));
                    }}
                    style={{ margin: 0 }}>
                    <option value="">— select —</option>
                    {editContactsForCompany.map(c => <option key={c.id} value={c.name}>{c.name}</option>)}
                  </select>
                ) : (
                  <input className="adm-input" value={editForm.clientName} onChange={e => setEditForm(f => ({ ...f, clientName: e.target.value }))} style={{ margin: 0 }} />
                )}
              </div>
              <div style={{ gridColumn: '1/-1' }}>
                <label className="adm-label">Contact Email</label>
                <input className="adm-input" type="email" value={editForm.clientEmail} onChange={e => setEditForm(f => ({ ...f, clientEmail: e.target.value }))} style={{ margin: 0 }} />
              </div>
              <div>
                <label className="adm-label">Date</label>
                <input className="adm-input" type="date" value={editForm.date} onChange={e => setEditForm(f => ({ ...f, date: e.target.value }))} style={{ margin: 0 }} />
              </div>
              <div>
                <label className="adm-label">Rate (€/h)</label>
                <input className="adm-input" type="number" placeholder="e.g. 85" value={editForm.hourlyRate} onChange={e => setEditForm(f => ({ ...f, hourlyRate: e.target.value }))} min="0" step="0.01" style={{ margin: 0 }} />
              </div>
              <div>
                <label className="adm-label">Start Time</label>
                <input className="adm-input" type="time" value={editForm.startTime} onChange={e => setEditForm(f => ({ ...f, startTime: e.target.value }))} style={{ margin: 0 }} />
              </div>
              <div>
                <label className="adm-label">End Time</label>
                <input className="adm-input" type="time" value={editForm.endTime} onChange={e => setEditForm(f => ({ ...f, endTime: e.target.value }))} style={{ margin: 0 }} />
              </div>
            </div>
            {calcEditHours(editForm.startTime, editForm.endTime) && (
              <div style={{ marginBottom: 12, fontSize: 13, color: '#6efcf5' }}>
                Total: <strong>{calcEditHours(editForm.startTime, editForm.endTime)}</strong>
                {editForm.hourlyRate && ` · €${(hoursToDecimal(calcEditHours(editForm.startTime, editForm.endTime)) * parseFloat(editForm.hourlyRate)).toFixed(2)}`}
              </div>
            )}
            <div style={{ marginBottom: 16 }}>
              <label className="adm-label">Notes</label>
              <textarea className="adm-input" rows={3} value={editForm.notes} onChange={e => setEditForm(f => ({ ...f, notes: e.target.value }))} style={{ margin: 0 }} />
            </div>
            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
              <button className="adm-btn adm-btn-sm" style={{ background: 'transparent', borderColor: 'rgba(255,255,255,0.15)', color: '#aaa' }} onClick={() => setEditEntry(null)}>Cancel</button>
              <button className="adm-btn" onClick={saveEdit} disabled={saving}>{saving ? 'Saving…' : '✓ Save Changes'}</button>
            </div>
          </div>
        </div>
      )}
      <div style={{ display: 'flex', gap: 10, marginBottom: 16, flexWrap: 'wrap', alignItems: 'center' }}>
        <input
          className="adm-input"
          style={{ maxWidth: 180, margin: 0 }}
          placeholder="Search…"
          value={search}
          onChange={e => { setSearch(e.target.value); setClientFilter(''); setCompanyFilter(''); }}
        />
        <select className="adm-input" style={{ maxWidth: 200, margin: 0 }} value={companyFilter}
          onChange={e => { setCompanyFilter(e.target.value); setClientFilter(''); setSearch(''); }}>
          <option value="">All companies</option>
          {companyNames.map(n => <option key={n} value={n}>{n}</option>)}
        </select>
        <select className="adm-input" style={{ maxWidth: 180, margin: 0 }} value={clientFilter}
          onChange={e => { setClientFilter(e.target.value); setCompanyFilter(''); setSearch(''); }}>
          <option value="">All contacts</option>
          {clientNames.map(n => <option key={n} value={n}>{n}</option>)}
        </select>
        {selected.size > 0 && (
          <button className="adm-btn adm-btn-sm" style={{ background: 'rgba(239,68,68,0.2)', borderColor: 'rgba(239,68,68,0.5)', color: '#ef4444', marginLeft: 'auto' }}
            onClick={deleteSelected} disabled={deleting}>
            {deleting ? 'Deleting…' : `🗑 Delete (${selected.size})`}
          </button>
        )}
      </div>

      {(companyFilter || clientFilter) && (() => {
        const label = companyFilter || clientFilter;
        const subset = companyFilter
          ? entries.filter(e => e.company_name === companyFilter)
          : entries.filter(e => e.client_name === clientFilter);
        const totalH = subset.reduce((acc, e) => acc + hoursToDecimal(e.total_hours), 0);
        const totalAmt = subset.reduce((acc, e) => { const a = calcAmount(e.total_hours, e.hourly_rate); return acc + (a ? parseFloat(a) : 0); }, 0);
        const confirmed = subset.filter(e => e.confirmed_at).length;
        return (
          <div className="adm-hl-summary-client">
            <span className="adm-hl-muted">{label}</span>
            <span>Total hours: <strong className="adm-hl-hours">{decimalToHours(totalH)}</strong></span>
            {totalAmt > 0 && <span>Total amount: <strong className="adm-hl-amount">€{totalAmt.toFixed(2)}</strong></span>}
            <span className="adm-hl-muted">{confirmed}/{subset.length} confirmed</span>
          </div>
        );
      })()}

      {selected.size > 0 && (
        <div className="adm-hl-summary-sel">
          <span className="adm-hl-muted">{selected.size} selected</span>
          <span>Total hours: <strong className="adm-hl-hours">{decimalToHours(totalSelHours)}</strong></span>
          {totalSelAmount > 0 && <span>Total amount: <strong className="adm-hl-amount">€{totalSelAmount.toFixed(2)}</strong></span>}
        </div>
      )}

      {loading ? <p style={{ color: '#aaa' }}>Loading…</p> : filtered.length === 0 ? (
        <p style={{ color: '#aaa' }}>No entries found.</p>
      ) : (
        <div className="adm-table-wrap">
          <table className="adm-table">
            <thead>
              <tr>
                <th style={{ width: 36 }}>
                  <input type="checkbox" checked={allChecked} onChange={toggleAll} style={{ cursor: 'pointer' }} />
                </th>
                <th>Company</th>
                <th>Contact</th>
                <th>Date</th>
                <th>Hours</th>
                <th>Rate (€/h)</th>
                <th>Amount (€)</th>
                <th>Status</th>
                <th>Confirmed</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(e => {
                const confirmed = !!e.confirmed_at;
                const isSelected = selected.has(e.id);
                const date = new Date(e.work_date).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
                const confirmedDate = e.confirmed_at ? new Date(e.confirmed_at).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : '—';
                const amount = calcAmount(e.total_hours, e.hourly_rate);
                return (
                  <tr key={e.id} onClick={() => toggleRow(e.id)} style={{ cursor: 'pointer', background: isSelected ? 'rgba(110,66,245,0.1)' : undefined }}>
                    <td onClick={ev => ev.stopPropagation()}>
                      <input type="checkbox" checked={isSelected} onChange={() => toggleRow(e.id)} style={{ cursor: 'pointer' }} />
                    </td>
                    <td>
                      {e.company_name
                        ? <div style={{ fontWeight: 600 }}>{e.company_name}</div>
                        : <div className="adm-hl-muted">—</div>}
                    </td>
                    <td>
                      <div style={{ fontWeight: 600 }}>{e.client_name}</div>
                      <div className="adm-hl-muted">{e.client_email}</div>
                    </td>
                    <td>{date}</td>
                    <td className="adm-hl-hours">{e.total_hours}</td>
                    <td className="adm-hl-muted">{e.hourly_rate ? `€${parseFloat(e.hourly_rate).toFixed(2)}` : '—'}</td>
                    <td className={amount ? 'adm-hl-amount' : 'adm-hl-muted'}>{amount ? `€${amount}` : '—'}</td>
                    <td>
                      <span style={{
                        display: 'inline-block', padding: '3px 10px', borderRadius: 20, fontSize: 12,
                        background: confirmed ? 'rgba(34,197,94,0.15)' : 'rgba(251,191,36,0.15)',
                        color: confirmed ? '#22c55e' : '#fbbf24',
                        border: `1px solid ${confirmed ? 'rgba(34,197,94,0.3)' : 'rgba(251,191,36,0.3)'}`,
                      }}>
                        {confirmed ? '✓ Confirmed' : '⏳ Pending'}
                      </span>
                    </td>
                    <td className="adm-hl-muted">{confirmedDate}</td>
                    <td onClick={ev => ev.stopPropagation()} style={{ whiteSpace: 'nowrap' }}>
                      <button className="adm-btn adm-btn-sm" style={{ marginRight: 6 }} onClick={ev => openEdit(e, ev)}>✎ Edit</button>
                      {!confirmed && (
                        <button className="adm-btn adm-btn-sm" onClick={() => sendReminder(e.id)} disabled={reminding === e.id}>
                          {reminding === e.id ? '…' : '↩ Remind'}
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ── Tools View ──
function Tools({ token, onTokenChange }) {
  const [confirm, setConfirm] = useState(null);
  const [done, setDone] = useState('');
  const [pwForm, setPwForm] = useState({ current: '', next: '', confirm: '' });
  const [pwError, setPwError] = useState('');
  const [pwDone, setPwDone] = useState(false);

  const changePassword = async () => {
    setPwError(''); setPwDone(false);
    if (!pwForm.current || !pwForm.next || !pwForm.confirm) return setPwError('All fields are required.');
    if (pwForm.next !== pwForm.confirm) return setPwError('New passwords do not match.');
    if (pwForm.next.length < 8) return setPwError('New password must be at least 8 characters.');
    const res = await fetch(`${API}/admin/change-password`, {
      method: 'POST',
      headers: { 'x-admin-token': token, 'Content-Type': 'application/json' },
      body: JSON.stringify({ currentPassword: pwForm.current, newPassword: pwForm.next }),
    });
    const data = await res.json();
    if (!res.ok) return setPwError(data.error || 'Failed to change password.');
    onTokenChange(data.token);
    setPwDone(true);
    setPwForm({ current: '', next: '', confirm: '' });
  };

  const exportCSV = async () => {
    const res = await fetch(`${API}/admin/export`, { headers: { 'x-admin-token': token } });
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'contacts.csv';
    a.click();
    URL.revokeObjectURL(url);
  };

  const clearRead = async () => {
    await fetch(`${API}/admin/contacts/clear/read`, {
      method: 'DELETE',
      headers: { 'x-admin-token': token },
    });
    setConfirm(null);
    setDone('All read messages have been cleared.');
    setTimeout(() => setDone(''), 4000);
  };

  return (
    <div>
      <h2 className="adm-page-title">Tools</h2>
      <p className="adm-page-sub">Manage and export your data</p>

      {done && (
        <div className="adm-error" style={{ background: 'rgba(110,252,245,0.08)', borderColor: 'rgba(110,252,245,0.3)', color: 'var(--adm-cyan)', marginBottom: 20 }}>
          {done}
        </div>
      )}

      <div className="adm-tools-grid">
        <div className="adm-tool-card">
          <div className="adm-tool-icon">⬇️</div>
          <p className="adm-tool-title">Export CSV</p>
          <p className="adm-tool-desc">Download all contact submissions as a CSV file for use in Excel or other tools.</p>
          <button className="adm-btn adm-btn-sm" onClick={exportCSV}>Download CSV</button>
        </div>

        <div className="adm-tool-card">
          <div className="adm-tool-icon">🗑️</div>
          <p className="adm-tool-title">Clear Read Messages</p>
          <p className="adm-tool-desc">Permanently delete all messages that have already been marked as read.</p>
          <button className="adm-btn adm-btn-danger adm-btn-sm" onClick={() => setConfirm('clearRead')}>
            Clear Read
          </button>
        </div>

        <div className="adm-tool-card">
          <div className="adm-tool-icon">✦</div>
          <p className="adm-tool-title">Mitra</p>
          <p className="adm-tool-desc">Strategic IT Consultancy landing page — services, values, and pricing for Mittelstand clients.</p>
          <button className="adm-btn adm-btn-sm" onClick={() => window.open('/mitra', '_blank')}>
            Open Mitra Page
          </button>
        </div>

      </div>

      {/* Change Password */}
      <div style={{ marginTop: 32, maxWidth: 420 }}>
        <h3 className="adm-section-title" style={{ marginBottom: 16 }}>Change Admin Password</h3>
        {pwDone && <div className="adm-error" style={{ background: 'rgba(110,252,245,0.08)', borderColor: 'rgba(110,252,245,0.3)', color: 'var(--adm-cyan)', marginBottom: 14 }}>Password changed successfully.</div>}
        {pwError && <div className="adm-error" style={{ marginBottom: 14 }}>{pwError}</div>}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          <input className="adm-input" type="password" placeholder="Current password" value={pwForm.current} onChange={e => setPwForm(f => ({ ...f, current: e.target.value }))} />
          <input className="adm-input" type="password" placeholder="New password" value={pwForm.next} onChange={e => setPwForm(f => ({ ...f, next: e.target.value }))} />
          <input className="adm-input" type="password" placeholder="Confirm new password" value={pwForm.confirm} onChange={e => setPwForm(f => ({ ...f, confirm: e.target.value }))} />
          <button className="adm-btn adm-btn-sm" style={{ alignSelf: 'flex-start' }} onClick={changePassword}>Save New Password</button>
        </div>
      </div>

      {confirm === 'clearRead' && (
        <div className="adm-confirm-overlay">
          <div className="adm-confirm-box">
            <p className="adm-confirm-title">Clear all read messages?</p>
            <p className="adm-confirm-text">This will permanently delete all messages marked as read. This action cannot be undone.</p>
            <div className="adm-confirm-actions">
              <button className="adm-btn adm-btn-ghost adm-btn-sm" onClick={() => setConfirm(null)}>Cancel</button>
              <button className="adm-btn adm-btn-danger adm-btn-sm" onClick={clearRead}>Clear</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ── QR Code View ──
function QRGenerator() {
  const [qrUrl, setQrUrl] = useState('');
  const [qrDataUrl, setQrDataUrl] = useState('');
  const [qrError, setQrError] = useState('');
  const [loading, setLoading] = useState(false);

  const generate = async () => {
    setQrError('');
    setQrDataUrl('');
    if (!qrUrl.trim()) { setQrError('Please enter a URL.'); return; }
    setLoading(true);
    try {
      const url = qrUrl.trim().startsWith('http') ? qrUrl.trim() : `https://${qrUrl.trim()}`;
      const dataUrl = await QRCode.toDataURL(url, {
        width: 400,
        margin: 2,
        color: { dark: '#000000', light: '#ffffff' },
      });
      setQrDataUrl(dataUrl);
    } catch {
      setQrError('Failed to generate QR code. Check the URL and try again.');
    } finally {
      setLoading(false);
    }
  };

  const download = (format) => {
    if (!qrDataUrl) return;
    if (format === 'png') {
      const a = document.createElement('a');
      a.href = qrDataUrl;
      a.download = 'qrcode.png';
      a.click();
    } else {
      const img = new Image();
      img.onload = () => {
        const canvas = document.createElement('canvas');
        canvas.width = img.width;
        canvas.height = img.height;
        const ctx = canvas.getContext('2d');
        ctx.fillStyle = '#ffffff';
        ctx.fillRect(0, 0, canvas.width, canvas.height);
        ctx.drawImage(img, 0, 0);
        const a = document.createElement('a');
        a.href = canvas.toDataURL('image/jpeg', 0.95);
        a.download = 'qrcode.jpg';
        a.click();
      };
      img.src = qrDataUrl;
    }
  };

  return (
    <div>
      <h2 className="adm-page-title">QR Code Generator</h2>
      <p className="adm-page-sub">Generate a QR code for any URL and download it as PNG or JPG</p>

      <div className="adm-qr-card">
        <label className="adm-qr-label">Website URL</label>
        <div className="adm-qr-row">
          <input
            className="adm-input"
            type="text"
            placeholder="https://7elemente.com"
            value={qrUrl}
            onChange={e => setQrUrl(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && generate()}
          />
          <button className="adm-btn" style={{ whiteSpace: 'nowrap' }} onClick={generate} disabled={loading}>
            {loading ? <span className="adm-spinner" /> : 'Generate'}
          </button>
        </div>
        {qrError && <p className="adm-qr-error">{qrError}</p>}

        {qrDataUrl && (
          <div className="adm-qr-result">
            <div className="adm-qr-img-wrap">
              <img src={qrDataUrl} alt="QR Code" className="adm-qr-img-lg" />
            </div>
            <div className="adm-qr-info">
              <p className="adm-qr-url-display">{qrUrl.trim().startsWith('http') ? qrUrl.trim() : `https://${qrUrl.trim()}`}</p>
              <p className="adm-qr-hint">Scan with any mobile camera to verify the QR code works.</p>
              <div className="adm-qr-dl-row">
                <button className="adm-btn" onClick={() => download('png')}>⬇ Download PNG</button>
                <button className="adm-btn adm-btn-ghost" onClick={() => download('jpg')}>⬇ Download JPG</button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ── Client Contracts ──
function ctFmt(iso) {
  if (!iso) return '—';
  return new Date(iso).toLocaleString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
}

// Renders as a styled <label> so a hidden <input type="file"> triggers on click.
// onUpload receives the full updated document row returned by the server — the
// caller must replace the item in its local state with this value.
function CtUploadBtn({ label = '⬆ Upload', uploadUrl, adminToken, onUpload }) {
  const [uploading, setUploading] = useState(false);
  const [count, setCount] = useState(0);
  const handleChange = async (e) => {
    const files = Array.from(e.target.files);
    if (!files.length) return;
    setUploading(true);
    setCount(files.length);
    try {
      const fd = new FormData();
      files.forEach(f => fd.append('files', f));
      const res = await fetch(uploadUrl, { method: 'POST', headers: { 'x-admin-token': adminToken }, body: fd });
      if (res.ok) onUpload(await res.json());
    } finally { setUploading(false); setCount(0); e.target.value = ''; }
  };
  return (
    <label className="adm-btn adm-btn-sm" style={{ cursor: uploading ? 'wait' : 'pointer', display: 'inline-flex', alignItems: 'center', gap: 6, opacity: uploading ? 0.7 : 1, width: 'fit-content' }}>
      {uploading
        ? <><span className="adm-spinner" style={{ width: 12, height: 12, borderWidth: 2 }} /> {count > 1 ? `Uploading ${count} files…` : 'Uploading…'}</>
        : label}
      <input type="file" multiple accept=".pdf,.doc,.docx,.png,.jpg,.jpeg,.gif,.webp,.bmp,.tiff" style={{ display: 'none' }} onChange={handleChange} disabled={uploading} />
    </label>
  );
}

function CcBadge({ status }) {
  const map = {
    draft:    ['Draft',    '#a78bfa', 'rgba(167,139,250,0.12)'],
    signed:   ['Signed',   '#6efcf5', 'rgba(110,252,245,0.12)'],
    final:    ['Final',    '#f39c12', 'rgba(243,156,18,0.12)'],
    complete: ['Complete', '#3bdfb5', 'rgba(59,223,181,0.12)'],
    sent:     ['Sent',     '#3bdfb5', 'rgba(59,223,181,0.12)'],
  };
  const [label, color, bg] = map[status] || ['New', '#888', 'rgba(136,136,136,0.12)'];
  return <span style={{ fontSize: 13, fontWeight: 600, letterSpacing: 1, textTransform: 'uppercase', color, background: bg, padding: '2px 9px', borderRadius: 99 }}>{label}</span>;
}

function CcDocSteps({ doc, docType, token, onUpdate }) {
  const base = `/api/admin/${docType}s/${doc.id}`;

  const fetchFile = async (filename) => {
    const res = await fetch(`/api/admin/uploads/${encodeURIComponent(filename)}`, { headers: { 'x-admin-token': token } });
    const blob = await res.blob();
    return URL.createObjectURL(blob);
  };
  const download = async (filename) => {
    try {
      const url = await fetchFile(filename);
      const a = document.createElement('a');
      a.href = url; a.download = filename.replace(/^\d+_/, '');
      a.click(); setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch {}
  };
  const viewFile = async (filename) => {
    try {
      const url = await fetchFile(filename);
      const w = window.open('', '_blank');
      if (w) w.location.href = url;
    } catch {}
  };

  const parseFiles = (raw) => {
    if (!raw) return [];
    try { const p = JSON.parse(raw); return Array.isArray(p) ? p : [p]; } catch { return [raw]; }
  };

  const signedFiles = parseFiles(doc.signed_file);

  return (
    <div style={{ marginBottom: 12 }}>
      <div style={{ fontSize: 12, fontWeight: 700, color: '#888', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 10 }}>
        Signed Document
        {signedFiles.length > 0 && <span style={{ marginLeft: 8, color: '#3bdfb5', fontWeight: 600 }}>✓ on file</span>}
      </div>
      {signedFiles.length > 0 && (
        <div style={{ marginBottom: 10 }}>
          {signedFiles.map(f => (
            <div key={f} style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 5,
              padding: '6px 10px', borderRadius: 7, background: 'rgba(59,223,181,0.07)', border: '1px solid rgba(59,223,181,0.2)' }}>
              <span style={{ fontSize: 12, color: '#888', flex: 1 }}>📎 {f.replace(/^\d+_/, '')}</span>
              <button className="adm-btn adm-btn-ghost adm-btn-sm" onClick={() => viewFile(f)}>View</button>
              <button className="adm-btn adm-btn-ghost adm-btn-sm" onClick={() => download(f)}>Download</button>
            </div>
          ))}
        </div>
      )}
      <CtUploadBtn
        label={signedFiles.length > 0 ? '⬆ Replace / Add Document' : '⬆ Upload Signed Document'}
        uploadUrl={`${base}/upload-signed`} adminToken={token} onUpload={onUpdate}
      />
    </div>
  );
}

function ClientDetails({ token }) {
  const H  = { 'x-admin-token': token };
  const HJ = { ...H, 'Content-Type': 'application/json' };

  const [clients,    setClients]    = useState([]);
  const [msas,       setMsas]       = useState([]);
  const [sows,       setSows]       = useState([]);
  const [loading,    setLoading]    = useState(true);
  const [search,     setSearch]     = useState('');
  const [msg,        setMsg]        = useState({ text: '', err: false });
  const [expandedId, setExpandedId] = useState(null);

  // add new client form
  const [showAddForm, setShowAddForm] = useState(false);
  const [addForm,     setAddForm]     = useState({ company: '', name: '', email: '', phone: '', address: '' });
  const [addErrors,   setAddErrors]   = useState({});
  const [addSaving,   setAddSaving]   = useState(false);

  // edit client modal
  const [editClient,       setEditClient]       = useState(null);
  const [editForm,         setEditForm]         = useState({});
  const [saving,           setSaving]           = useState(false);
  const [confirmDelClient, setConfirmDelClient] = useState(null);

  const flash = (text, err = false) => { setMsg({ text, err }); setTimeout(() => setMsg({ text: '', err: false }), 3000); };

  const load = () => {
    Promise.all([
      fetch(`${API}/admin/clients`, { headers: H }).then(r => r.json()),
      fetch(`${API}/admin/msas`,    { headers: H }).then(r => r.json()),
      fetch(`${API}/admin/sows`,    { headers: H }).then(r => r.json()),
    ]).then(([c, m, s]) => {
      setClients(Array.isArray(c) ? c : []);
      setMsas(Array.isArray(m) ? m : []);
      setSows(Array.isArray(s) ? s : []);
      setLoading(false);
    }).catch(() => setLoading(false));
  };
  useEffect(load, [token]); // eslint-disable-line react-hooks/exhaustive-deps

  const validateAdd = () => {
    const errs = {};
    if (!addForm.company.trim()) errs.company = 'Company name is required';
    if (!addForm.name.trim())    errs.name    = 'Contact name is required';
    if (addForm.email.trim() && !validEmail(addForm.email)) errs.email = 'Enter a valid email';
    setAddErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const submitAdd = async () => {
    if (!validateAdd()) return;
    setAddSaving(true);
    const res = await fetch(`${API}/admin/clients`, { method: 'POST', headers: HJ, body: JSON.stringify(addForm) });
    setAddSaving(false);
    if (res.ok) {
      const c = await res.json();
      setClients(cs => [c, ...cs]);
      setShowAddForm(false);
      setAddForm({ company: '', name: '', email: '', phone: '', address: '' });
      setAddErrors({});
      flash('Client added successfully.');
    } else { flash('Failed to add client.', true); }
  };

  const saveClient = async () => {
    setSaving(true);
    const res = await fetch(`${API}/admin/clients/${editClient.id}`, { method: 'PUT', headers: HJ, body: JSON.stringify(editForm) });
    setSaving(false);
    if (res.ok) { load(); setEditClient(null); flash('Client updated.'); }
  };

  const deleteClient = async (id) => {
    const res = await fetch(`${API}/admin/clients/${id}`, { method: 'DELETE', headers: H });
    if (res.ok) { setClients(cs => cs.filter(c => c.id !== id)); setConfirmDelClient(null); flash('Client deleted.'); }
    else flash('Delete failed.', true);
  };

  const filtered = clients.filter(c => {
    const q = search.toLowerCase();
    return !q || c.name.toLowerCase().includes(q) || (c.company||'').toLowerCase().includes(q) || (c.email||'').toLowerCase().includes(q);
  });

  const statusDot = (status) => {
    const color = status === 'complete' ? '#3bdfb5' : status === 'signed' ? '#6efcf5' : '#f39c12';
    return <span style={{ display:'inline-block', width:7, height:7, borderRadius:'50%', background:color, marginRight:5, verticalAlign:'middle' }} />;
  };

  return (
    <div className="adm-section">
      <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:20, flexWrap:'wrap', gap:12 }}>
        <div>
          <h2 className="adm-page-title" style={{ margin:0 }}>Clients</h2>
          <p className="adm-page-sub" style={{ margin:'4px 0 0' }}>Manage clients and view their connected MSAs and SOWs</p>
        </div>
        <button className="adm-btn adm-btn-sm" onClick={() => setShowAddForm(v => !v)}>+ Add Client</button>
      </div>

      {msg.text && (
        <div style={{ marginBottom:12, padding:'8px 14px', borderRadius:8, fontSize:13,
          background: msg.err ? 'rgba(252,0,113,0.1)' : 'rgba(110,252,245,0.08)',
          color: msg.err ? '#fc0071' : '#6efcf5',
          border: `1px solid ${msg.err ? 'rgba(252,0,113,0.3)' : 'rgba(110,252,245,0.2)'}` }}>
          {msg.text}
        </div>
      )}

      {/* Add client form */}
      {showAddForm && (
        <div className="ct-inline-form" style={{ marginBottom:20 }}>
          <h4 style={{ margin:'0 0 16px', fontSize:15 }}>New Client</h4>
          <div className="ct-form-grid">
            <div className="ct-field ct-field-wide">
              <label className="ct-label">Company Name *</label>
              <input className={`adm-input${addErrors.company ? ' adm-input--error' : ''}`} value={addForm.company}
                onChange={e => { setAddForm(f => ({ ...f, company: e.target.value })); setAddErrors(er => ({ ...er, company: '' })); }}
                placeholder="Acme Corp GmbH" autoFocus />
              {addErrors.company && <div className="adm-field-error">{addErrors.company}</div>}
            </div>
            <div className="ct-field ct-field-wide">
              <label className="ct-label">Contact Person *</label>
              <input className={`adm-input${addErrors.name ? ' adm-input--error' : ''}`} value={addForm.name}
                onChange={e => { setAddForm(f => ({ ...f, name: e.target.value })); setAddErrors(er => ({ ...er, name: '' })); }}
                placeholder="John Doe" />
              {addErrors.name && <div className="adm-field-error">{addErrors.name}</div>}
            </div>
            <div className="ct-field">
              <label className="ct-label">Email</label>
              <input className={`adm-input${addErrors.email ? ' adm-input--error' : ''}`} type="email" value={addForm.email}
                onChange={e => { setAddForm(f => ({ ...f, email: e.target.value })); setAddErrors(er => ({ ...er, email: '' })); }}
                placeholder="john@acme.com" />
              {addErrors.email && <div className="adm-field-error">{addErrors.email}</div>}
            </div>
            <div className="ct-field">
              <label className="ct-label">Phone</label>
              <input className="adm-input" type="tel" value={addForm.phone}
                onChange={e => setAddForm(f => ({ ...f, phone: e.target.value }))} placeholder="+49 123 456789" />
            </div>
            <div className="ct-field ct-field-wide">
              <label className="ct-label">Address</label>
              <textarea className="adm-input" rows={2} value={addForm.address}
                onChange={e => setAddForm(f => ({ ...f, address: e.target.value }))}
                placeholder="Street, City, Country" style={{ resize: 'vertical', minHeight: 56 }} />
            </div>
          </div>
          <div className="ct-form-actions">
            <button className="adm-btn adm-btn-ghost adm-btn-sm" onClick={() => { setShowAddForm(false); setAddForm({ company:'', name:'', email:'', phone:'', address:'' }); setAddErrors({}); }}>Cancel</button>
            <button className="adm-btn adm-btn-sm" disabled={addSaving} onClick={submitAdd}>{addSaving ? 'Saving…' : 'Save Client'}</button>
          </div>
        </div>
      )}

      <input className="adm-input" style={{ maxWidth:280, marginBottom:20 }}
        placeholder="Search by name, company or email…" value={search} onChange={e => setSearch(e.target.value)} />

      {/* Edit client modal */}
      {editClient && (
        <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.7)', zIndex:9999, display:'flex', alignItems:'center', justifyContent:'center' }}
          onClick={() => setEditClient(null)}>
          <div style={{ background:'#1a1a2e', border:'1px solid rgba(110,66,245,0.3)', borderRadius:14, padding:28, width:'100%', maxWidth:440, margin:'0 16px' }}
            onClick={e => e.stopPropagation()}>
            <h3 style={{ margin:'0 0 20px', fontSize:18 }}>Edit — {editClient.company || editClient.name}</h3>
            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12, marginBottom:16 }}>
              {[['name','Contact Name'],['company','Company'],['email','Email'],['phone','Phone']].map(([k,l]) => (
                <div key={k} style={{ gridColumn: k==='email'?'1/-1':undefined }}>
                  <label className="adm-label">{l}</label>
                  <input className="adm-input" value={editForm[k]||''} onChange={e => setEditForm(f => ({ ...f, [k]: e.target.value }))} style={{ margin:0 }} />
                </div>
              ))}
              <div style={{ gridColumn:'1/-1' }}>
                <label className="adm-label">Address</label>
                <textarea className="adm-input" rows={2} value={editForm.address||''} onChange={e => setEditForm(f => ({ ...f, address: e.target.value }))} style={{ margin:0, resize:'vertical', minHeight:56 }} />
              </div>
            </div>
            <div style={{ display:'flex', gap:10, justifyContent:'flex-end' }}>
              <button className="adm-btn adm-btn-ghost adm-btn-sm" onClick={() => setEditClient(null)}>Cancel</button>
              <button className="adm-btn adm-btn-sm" onClick={saveClient} disabled={saving}>{saving ? 'Saving…' : '✓ Save'}</button>
            </div>
          </div>
        </div>
      )}

      {loading ? <p style={{ color:'#aaa' }}>Loading…</p> : filtered.length === 0 ? (
        <div className="adm-empty">{clients.length === 0 ? 'No clients yet. Click "+ Add Client" to get started.' : 'No clients match your search.'}</div>
      ) : (
        <div className="ct-list">
          {filtered.map(c => {
            const clientMSAs = msas.filter(m => String(m.client_id) === String(c.id));
            const clientSOWs = sows.filter(s => String(s.client_id) === String(c.id));
            const isOpen = expandedId === c.id;
            return (
              <div key={c.id} className={`ct-row${isOpen ? ' ct-row-open' : ''}`}>
                <div className="ct-row-main" onClick={() => setExpandedId(isOpen ? null : c.id)}>
                  <div className="ct-row-left">
                    <div style={{ width:36, height:36, borderRadius:'50%', background:'rgba(110,66,245,0.2)',
                      display:'flex', alignItems:'center', justifyContent:'center',
                      fontSize:16, fontWeight:700, color:'#a78bfa', flexShrink:0 }}>
                      {(c.company || c.name).charAt(0).toUpperCase()}
                    </div>
                    <div className="ct-cns-info">
                      <div className="ct-title">{c.company || c.name}</div>
                      <div className="ct-cns-meta">
                        <span className="ct-cns-email">{c.name}</span>
                        {c.email && <span className="ct-cns-phone">{c.email}</span>}
                        {c.phone && <span className="ct-cns-nda">{c.phone}</span>}
                      </div>
                    </div>
                  </div>
                  <div className="ct-row-right">
                    <span style={{ fontSize:12, color:'#a78bfa' }}>{clientMSAs.length} MSA{clientMSAs.length !== 1 ? 's' : ''}</span>
                    <span style={{ fontSize:12, color:'#fc0071' }}>{clientSOWs.length} SOW{clientSOWs.length !== 1 ? 's' : ''}</span>
                    <span className="ct-chevron">{isOpen ? '▲' : '▼'}</span>
                  </div>
                </div>

                {isOpen && (
                  <div className="ct-detail" onClick={e => e.stopPropagation()}>
                    {/* Contact details */}
                    <div style={{ display:'flex', gap:24, flexWrap:'wrap', marginBottom:16, paddingBottom:16, borderBottom:'1px solid rgba(255,255,255,0.07)' }}>
                      {[['Contact', c.name], ['Email', c.email], ['Phone', c.phone], ['Address', c.address]].map(([label, val]) => val ? (
                        <div key={label}>
                          <div style={{ fontSize:11, color:'#888', marginBottom:2 }}>{label}</div>
                          <div style={{ fontSize:13, color:'var(--adm-text)' }}>
                            {label === 'Email' ? <a href={`mailto:${val}`} style={{ color:'var(--adm-cyan,#6efcf5)', textDecoration:'none' }}>{val}</a> : val}
                          </div>
                        </div>
                      ) : null)}
                    </div>

                    {/* MSAs */}
                    <div style={{ marginBottom:14 }}>
                      <div style={{ fontSize:12, fontWeight:700, color:'#a78bfa', textTransform:'uppercase', letterSpacing:1, marginBottom:8 }}>Master Service Agreements</div>
                      {clientMSAs.length === 0
                        ? <div style={{ fontSize:13, color:'#555' }}>No MSAs — create one from the MSA section.</div>
                        : clientMSAs.map(m => (
                          <div key={m.id} style={{ display:'flex', alignItems:'center', gap:10, marginBottom:6,
                            padding:'8px 12px', borderRadius:8, background:'rgba(167,139,250,0.07)', border:'1px solid rgba(167,139,250,0.15)' }}>
                            {statusDot(m.status)}
                            <span style={{ fontFamily:'monospace', fontSize:13, color:'#a78bfa', fontWeight:600 }}>{m.msa_number}</span>
                            <span style={{ fontSize:12, color:'#888', textTransform:'capitalize' }}>{m.status || 'draft'}</span>
                          </div>
                        ))
                      }
                    </div>

                    {/* SOWs */}
                    <div style={{ marginBottom:14 }}>
                      <div style={{ fontSize:12, fontWeight:700, color:'#fc0071', textTransform:'uppercase', letterSpacing:1, marginBottom:8 }}>Statements of Work</div>
                      {clientSOWs.length === 0
                        ? <div style={{ fontSize:13, color:'#555' }}>No SOWs — create one from the SOW section.</div>
                        : clientSOWs.map(s => (
                          <div key={s.id} style={{ display:'flex', alignItems:'center', gap:10, marginBottom:6,
                            padding:'8px 12px', borderRadius:8, background:'rgba(252,0,113,0.06)', border:'1px solid rgba(252,0,113,0.15)' }}>
                            {statusDot(s.status)}
                            <span style={{ fontFamily:'monospace', fontSize:13, color:'#fc0071', fontWeight:600 }}>{s.sow_number}</span>
                            <span style={{ fontSize:13, color:'var(--adm-text)', flex:1 }}>{s.project_name}</span>
                            {s.cost_of_project != null && (
                              <span style={{ fontSize:12, color:'#888' }}>€{parseFloat(s.cost_of_project).toLocaleString()}</span>
                            )}
                          </div>
                        ))
                      }
                    </div>

                    <div className="ct-detail-actions">
                      <button className="adm-btn adm-btn-ghost adm-btn-sm" onClick={() => { setEditClient(c); setEditForm({ name:c.name, company:c.company||'', email:c.email||'', phone:c.phone||'', address:c.address||'' }); }}>✎ Edit Client</button>
                      <button className="adm-btn adm-btn-danger adm-btn-sm" onClick={() => setConfirmDelClient(c)}>Delete Client</button>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Delete client confirmation */}
      {confirmDelClient && (
        <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.75)', zIndex:9999, display:'flex', alignItems:'center', justifyContent:'center' }}
          onClick={() => setConfirmDelClient(null)}>
          <div style={{ background:'#1a1a2e', border:'1px solid rgba(252,0,113,0.3)', borderRadius:14, padding:28, width:'100%', maxWidth:400, margin:'0 16px' }}
            onClick={e => e.stopPropagation()}>
            <h3 style={{ margin:'0 0 10px', fontSize:17 }}>Delete Client</h3>
            <p style={{ margin:'0 0 20px', fontSize:14, color:'#aaa' }}>
              Delete <strong>{confirmDelClient.company || confirmDelClient.name}</strong>? This cannot be undone. Any linked MSAs and SOWs will remain but lose the client reference.
            </p>
            <div style={{ display:'flex', gap:10, justifyContent:'flex-end' }}>
              <button className="adm-btn adm-btn-ghost adm-btn-sm" onClick={() => setConfirmDelClient(null)}>Cancel</button>
              <button className="adm-btn adm-btn-danger adm-btn-sm" onClick={() => deleteClient(confirmDelClient.id)}>Delete</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function ContractsMSA({ token }) {
  const [msas, setMsas] = useState([]);
  const [clients, setClients] = useState([]);
  const [sows, setSows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [formClientId, setFormClientId] = useState('');
  const [isNewClient, setIsNewClient] = useState(false);
  const [newClient, setNewClient] = useState({ company: '', name: '', email: '', phone: '' });
  const [newClientErrors, setNewClientErrors] = useState({});
  const [msaNumberInput, setMsaNumberInput] = useState('');
  const [msaSignedFile, setMsaSignedFile] = useState(null);
  const [expandedId, setExpandedId] = useState(null);
  const [confirmDel, setConfirmDel] = useState(null);
  const [toast, setToast] = useState(null);
  const [creating, setCreating] = useState(false);

  const H = { 'x-admin-token': token };
  const showToast = (msg, isErr = false) => { setToast({ msg, isErr }); setTimeout(() => setToast(null), 3200); };

  const loadAll = useCallback(async () => {
    try {
      const [cr, mr, sr] = await Promise.all([
        fetch('/api/admin/clients', { headers: H }).then(r => r.json()),
        fetch('/api/admin/msas',    { headers: H }).then(r => r.json()),
        fetch('/api/admin/sows',    { headers: H }).then(r => r.json()),
      ]);
      setClients(Array.isArray(cr) ? cr : []);
      setMsas(Array.isArray(mr) ? mr : []);
      setSows(Array.isArray(sr) ? sr : []);
    } catch { showToast('Failed to load', true); }
    finally { setLoading(false); }
  }, [token]);

  useEffect(() => { loadAll(); }, [loadAll]);

  const validateNewClient = () => {
    const errs = {};
    if (!newClient.company.trim()) errs.company = 'Company name is required';
    if (!newClient.name.trim())    errs.name    = 'Contact person is required';
    if (newClient.email.trim() && !validEmail(newClient.email)) errs.email = 'Enter a valid email address';
    setNewClientErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const createMSA = async () => {
    if (isNewClient && !validateNewClient()) return;
    if (!isNewClient && !formClientId) { showToast('Please select a client', true); return; }
    setCreating(true);
    try {
      const HJ = { ...H, 'Content-Type': 'application/json' };
      let cid = formClientId;
      if (isNewClient) {
        const cr = await fetch('/api/admin/clients', { method: 'POST', headers: HJ, body: JSON.stringify(newClient) });
        if (!cr.ok) { showToast('Failed to create client', true); return; }
        const c = await cr.json();
        cid = c.id;
        setClients(cs => [c, ...cs]);
      }
      const res = await fetch('/api/admin/msas', { method: 'POST', headers: HJ, body: JSON.stringify({ client_id: cid }) });
      if (res.ok) {
        let m = await res.json();
        if (msaNumberInput.trim()) {
          await fetch(`/api/admin/msas/${m.id}`, { method: 'PATCH', headers: HJ, body: JSON.stringify({ msa_number: msaNumberInput.trim() }) });
          m = { ...m, msa_number: msaNumberInput.trim() };
        }
        if (msaSignedFile) {
          const fd = new FormData();
          fd.append('files', msaSignedFile);
          await fetch(`/api/admin/msas/${m.id}/upload-signed`, { method: 'POST', headers: H, body: fd });
        }
        setMsas(ms => [m, ...ms]);
        setShowForm(false); setFormClientId(''); setIsNewClient(false);
        setNewClient({ company: '', name: '', email: '', phone: '' }); setNewClientErrors({});
        setMsaNumberInput(''); setMsaSignedFile(null);
        showToast(`MSA ${m.msa_number} created`);
      } else { const e = await res.json().catch(() => ({})); showToast(e.message || 'Failed', true); }
    } finally { setCreating(false); }
  };

  const deleteMSA = async () => {
    const res = await fetch(`/api/admin/msas/${confirmDel}`, { method: 'DELETE', headers: H });
    if (res.ok) { setMsas(ms => ms.filter(m => m.id !== confirmDel)); showToast('Deleted'); }
    else showToast('Delete failed', true);
    setConfirmDel(null);
  };

  const updateMSA = (m) => setMsas(ms => ms.map(x => x.id === m.id ? m : x));

  if (loading) return <div className="adm-empty">Loading\u2026</div>;

  const canCreate = isNewClient ? (newClient.company && newClient.name) : !!formClientId;

  return (
    <div>
      <div className="ct-header">
        <div>
          <h2 className="adm-page-title">Master Service Agreements</h2>
          <p className="adm-page-sub">Long-term client agreements (3 months+)</p>
        </div>
        <button className="adm-btn adm-btn-sm" onClick={() => setShowForm(v => !v)}>+ New MSA</button>
      </div>

      <div className="adm-stats" style={{ marginBottom: 20 }}>
        <div className="adm-stat-card"><div className="adm-stat-label">Total MSAs</div><div className="adm-stat-value">{msas.length}</div></div>
        <div className="adm-stat-card"><div className="adm-stat-label">Complete</div><div className="adm-stat-value">{msas.filter(m => m.status === 'complete').length}</div></div>
        <div className="adm-stat-card"><div className="adm-stat-label">In Progress</div><div className="adm-stat-value">{msas.filter(m => m.status !== 'complete').length}</div></div>
        <div className="adm-stat-card"><div className="adm-stat-label">Clients</div><div className="adm-stat-value">{clients.length}</div></div>
      </div>

      {showForm && (
        <div className="ct-inline-form" style={{ marginBottom: 20 }}>
          <div className="ct-form-grid">
            <div className="ct-field ct-field-wide">
              <label className="ct-label">Client *</label>
              <select className="adm-input" value={isNewClient ? '__new__' : formClientId} onChange={e => {
                if (e.target.value === '__new__') { setIsNewClient(true); setFormClientId(''); }
                else { setIsNewClient(false); setFormClientId(e.target.value); }
              }}>
                <option value="">— select existing client —</option>
                {clients.map(c => <option key={c.id} value={c.id}>{c.company} · {c.name}</option>)}
                <option value="__new__">+ Add new client</option>
              </select>
            </div>
            {isNewClient && (<>
              <div className="ct-field ct-field-wide">
                <label className="ct-label">Company Name *</label>
                <input className={`adm-input${newClientErrors.company ? ' adm-input--error' : ''}`} value={newClient.company}
                  onChange={e => { setNewClient(n => ({ ...n, company: e.target.value })); setNewClientErrors(er => ({ ...er, company: '' })); }}
                  placeholder="Acme Corp GmbH" />
                {newClientErrors.company && <div className="adm-field-error">{newClientErrors.company}</div>}
              </div>
              <div className="ct-field ct-field-wide">
                <label className="ct-label">Contact Person *</label>
                <input className={`adm-input${newClientErrors.name ? ' adm-input--error' : ''}`} value={newClient.name}
                  onChange={e => { setNewClient(n => ({ ...n, name: e.target.value })); setNewClientErrors(er => ({ ...er, name: '' })); }}
                  placeholder="John Doe" />
                {newClientErrors.name && <div className="adm-field-error">{newClientErrors.name}</div>}
              </div>
              <div className="ct-field">
                <label className="ct-label">Email</label>
                <input className={`adm-input${newClientErrors.email ? ' adm-input--error' : ''}`} type="email" value={newClient.email}
                  onChange={e => { setNewClient(n => ({ ...n, email: e.target.value })); setNewClientErrors(er => ({ ...er, email: '' })); }}
                  placeholder="john@acme.com" />
                {newClientErrors.email && <div className="adm-field-error">{newClientErrors.email}</div>}
              </div>
              <div className="ct-field">
                <label className="ct-label">Phone</label>
                <input className="adm-input" type="tel" value={newClient.phone} onChange={e => setNewClient(n => ({ ...n, phone: e.target.value }))} placeholder="+49 123 456789" />
              </div>
            </>)}
            <div className="ct-field">
              <label className="ct-label">MSA Number <span style={{ color: '#888', fontSize: 11 }}>\u2014 auto-generated if blank</span></label>
              <input className="adm-input" value={msaNumberInput} onChange={e => setMsaNumberInput(e.target.value)} placeholder="e.g. 7EITSM001" />
            </div>
            <div className="ct-field ct-field-wide">
              <label className="ct-label">Upload Signed MSA <span style={{ color: '#888', fontSize: 11 }}>\u2014 optional</span></label>
              <input className="adm-input" type="file" accept=".pdf,.doc,.docx,.png,.jpg,.jpeg,.gif,.webp,.bmp,.tiff"
                onChange={e => setMsaSignedFile(e.target.files[0] || null)} style={{ padding: '8px 12px', cursor: 'pointer' }} />
            </div>
          </div>
          <div className="ct-form-actions">
            <button className="adm-btn adm-btn-ghost adm-btn-sm" onClick={() => { setShowForm(false); setFormClientId(''); setIsNewClient(false); setNewClientErrors({}); setMsaNumberInput(''); setMsaSignedFile(null); }}>Cancel</button>
            <button className="adm-btn adm-btn-sm" disabled={creating} onClick={createMSA}>
              {creating ? 'Creating\u2026' : 'Generate MSA'}
            </button>
          </div>
        </div>
      )}

      {msas.length === 0 && !showForm
        ? <div className="adm-empty">No MSAs yet. Click "+ New MSA" to get started.</div>
        : <div className="ct-list">
            {msas.map(msa => {
              const msaSows = sows.filter(s => s.msa_id === msa.id);
              const isOpen = expandedId === msa.id;
              // completed_at first — avoids a false stepIdx if sent_at was skipped
              const stepIdx = msa.completed_at ? 4 : !msa.draft_file ? 0 : !msa.final_file ? 1 : !msa.sent_at ? 2 : 3;
              const stepLabel = ['Upload Draft', 'Upload Final', 'Email to Client', 'Mark Complete', 'Complete'][stepIdx];
              const stepColor = stepIdx === 4 ? '#3bdfb5' : '#f39c12'; // green = done, yellow = in-progress
              return (
                <div key={msa.id} className={`ct-row${isOpen ? ' ct-row-open' : ''}`}>
                  <div className="ct-row-main" onClick={() => setExpandedId(isOpen ? null : msa.id)}>
                    <div className="ct-row-left">
                      <div className="cns-step-dot" style={{ background: stepColor, boxShadow: `0 0 8px ${stepColor}40`, fontSize: 12 }}>{stepIdx === 4 ? '✓' : stepIdx + 1}</div>
                      <div className="ct-cns-info">
                        <div className="ct-title" style={{ fontFamily: 'monospace', fontSize: 14 }}>{msa.msa_number}</div>
                        <div className="ct-cns-meta">
                          <span className="ct-cns-email">{msa.company || msa.client_name}</span>
                          <span className="ct-cns-phone">{msa.client_name}</span>
                          {msaSows.length > 0 && <span className="ct-cns-nda">{msaSows.length} SOW{msaSows.length !== 1 ? 's' : ''}</span>}
                        </div>
                      </div>
                    </div>
                    <div className="ct-row-right">
                      <span style={{ fontSize: 13, fontWeight: 600, letterSpacing: 1, textTransform: 'uppercase',
                        color: stepColor, background: `${stepColor}1a`, padding: '3px 10px', borderRadius: 99 }}>
                        {stepLabel}
                      </span>
                      <span className="ct-ts">{ctFmt(msa.created_at)}</span>
                      <span className="ct-chevron">{isOpen ? '▲' : '▼'}</span>
                    </div>
                  </div>

                  {/* Mini progress strip */}
                  <div className="cns-prog-mini-wrap">
                    <div className="cns-prog-mini-fill" style={{ width: `${[0, 25, 50, 75, 100][stepIdx]}%`, background: stepColor }} />
                  </div>

                  {isOpen && (
                    <div className="ct-detail" onClick={e => e.stopPropagation()}>
                      <CcDocSteps doc={msa} docType="msa" token={token} onUpdate={updateMSA} />
                      <div className="ct-detail-actions">
                        <button className="adm-btn adm-btn-danger adm-btn-sm" onClick={e => { e.stopPropagation(); setConfirmDel(msa.id); }}>Delete MSA</button>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
      }

      {confirmDel && (
        <div className="adm-confirm-overlay">
          <div className="adm-confirm-box">
            <p className="adm-confirm-title">Delete MSA?</p>
            <p className="adm-confirm-text">This cannot be undone.</p>
            <div className="adm-confirm-actions">
              <button className="adm-btn adm-btn-ghost adm-btn-sm" onClick={() => setConfirmDel(null)}>Cancel</button>
              <button className="adm-btn adm-btn-danger adm-btn-sm" onClick={deleteMSA}>Delete</button>
            </div>
          </div>
        </div>
      )}
      {toast && <div className={`adm-toast${toast.isErr ? ' adm-toast-err' : ''}`}>{toast.msg}</div>}
    </div>
  );
}

const SOW_TIERS = [
  { key: 'tier1', label: 'Tier 1', name: 'Digital Presence & SEO',    icon: '🌐', desc: 'Websites, SEO, digital marketing & branding',      color: '#6efcf5' },
  { key: 'tier2', label: 'Tier 2', name: 'Custom App Development',     icon: '⚙️', desc: 'Web apps, mobile apps, custom software solutions', color: '#a78bfa' },
  { key: 'tier3', label: 'Tier 3', name: 'Enterprise Intel (ERP/CRM)', icon: '🏢', desc: 'Large-scale ERP, CRM and enterprise integrations',  color: '#f59e0b' },
];

function SowWizard({ clients, msas, token, onCreated, onCancel }) {
  const [longTerm, setLongTerm]       = useState(null);
  const [clientId, setClientId]       = useState('');
  const [isNewClient, setIsNewClient] = useState(false);
  const [newClient, setNewClient]     = useState({ company: '', name: '', email: '', phone: '' });
  const [projectName, setProjectName] = useState('');
  const [projectCost, setProjectCost] = useState('');
  const [hostingCost, setHostingCost] = useState('');
  const [msaId, setMsaId]             = useState('');
  const [sowNumber, setSowNumber]     = useState('');
  const [signedFile, setSignedFile]   = useState(null);
  const [submitting, setSubmitting]   = useState(false);
  const [error, setError]             = useState('');
  const [fieldErrors, setFieldErrors] = useState({});
  const H = { 'x-admin-token': token, 'Content-Type': 'application/json' };

  useEffect(() => {
    fetch('/api/admin/sows/next-number', { headers: { 'x-admin-token': token } })
      .then(r => r.json())
      .then(d => { if (d.number) setSowNumber(d.number); })
      .catch(() => {});
  }, [token]);

  const clientMSAs = msas.filter(m => String(m.client_id) === String(clientId));

  const validate = () => {
    const errs = {};
    if (longTerm === null) errs.duration = 'Please select project duration';
    if (!isNewClient && !clientId) errs.client = 'Please select a client';
    if (isNewClient && !newClient.company.trim()) errs.company = 'Company name is required';
    if (isNewClient && !newClient.name.trim())    errs.contactName = 'Contact person is required';
    if (isNewClient && newClient.email.trim() && !validEmail(newClient.email)) errs.email = 'Enter a valid email address';
    if (!projectName.trim()) errs.projectName = 'Project name is required';
    if (projectCost !== '' && (isNaN(parseFloat(projectCost)) || parseFloat(projectCost) < 0)) errs.projectCost = 'Enter a valid amount';
    if (hostingCost  !== '' && (isNaN(parseFloat(hostingCost))  || parseFloat(hostingCost)  < 0)) errs.hostingCost  = 'Enter a valid amount';
    setFieldErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const submit = async () => {
    if (!validate()) return;
    setError(''); setSubmitting(true);
    try {
      let cid = clientId;
      if (isNewClient) {
        const cr = await fetch('/api/admin/clients', { method: 'POST', headers: H, body: JSON.stringify(newClient) });
        if (!cr.ok) { const e = await cr.json().catch(() => ({})); setError(e.message || 'Failed to create client'); return; }
        cid = (await cr.json()).id;
      }
      const payload = {
        client_id: cid, project_name: projectName,
        duration_months: longTerm ? 3 : 1,
        ...(projectCost !== '' ? { cost_of_project: projectCost } : {}),
        ...(hostingCost  !== '' ? { cost_of_hosting:  hostingCost  } : {}),
        ...(longTerm && msaId ? { msa_id: msaId } : {}),
      };
      const res = await fetch('/api/admin/sows', { method: 'POST', headers: H, body: JSON.stringify(payload) });
      if (!res.ok) { const e = await res.json().catch(() => ({})); setError(e.message || 'Failed to create SOW'); return; }
      const created = await res.json();
      if (sowNumber.trim()) {
        await fetch(`/api/admin/sows/${created.id}`, { method: 'PATCH', headers: H, body: JSON.stringify({ sow_number: sowNumber.trim() }) });
        created.sow_number = sowNumber.trim();
      }
      if (signedFile) {
        const fd = new FormData();
        fd.append('files', signedFile);
        await fetch(`/api/admin/sows/${created.id}/upload-signed`, { method: 'POST', headers: { 'x-admin-token': token }, body: fd });
      }
      onCreated(created);
    } finally { setSubmitting(false); }
  };

  return (
    <div className="cc-wizard">
      {/* Duration */}
      <div style={{ marginBottom: 20 }}>
        <label className="ct-label" style={{ display: 'block', marginBottom: 10 }}>Project Duration *</label>
        <div className="cc-wizard-dur">
          <button
            className={`cc-wizard-dur-btn${longTerm === false ? ' cc-wizard-dur-btn-active' : ''}`}
            style={longTerm === false ? { borderColor: '#6efcf5', background: 'rgba(110,252,245,0.08)' } : {}}
            onClick={() => setLongTerm(false)}>
            <div className="cc-wizard-dur-icon">⚡</div>
            <div className="cc-wizard-dur-label">Under 3 months</div>
            <div className="cc-wizard-dur-sub">Quick project — SOW only</div>
          </button>
          <button
            className={`cc-wizard-dur-btn${longTerm === true ? ' cc-wizard-dur-btn-active' : ''}`}
            style={longTerm === true ? { borderColor: '#a78bfa', background: 'rgba(167,139,250,0.08)' } : {}}
            onClick={() => setLongTerm(true)}>
            <div className="cc-wizard-dur-icon">📋</div>
            <div className="cc-wizard-dur-label">3 months or more</div>
            <div className="cc-wizard-dur-sub">Long-term — MSA recommended</div>
          </button>
        </div>
        {fieldErrors.duration && <div className="adm-field-error" style={{ marginTop: 6 }}>{fieldErrors.duration}</div>}
      </div>

      <div className="ct-form-grid">
        {/* Client */}
        <div className="ct-field ct-field-wide">
          <label className="ct-label">Client *</label>
          <select className={`adm-input${fieldErrors.client ? ' adm-input--error' : ''}`} value={isNewClient ? '__new__' : clientId} onChange={e => {
            if (e.target.value === '__new__') { setIsNewClient(true); setClientId(''); setMsaId(''); }
            else { setIsNewClient(false); setClientId(e.target.value); setMsaId(''); }
            setFieldErrors(er => ({ ...er, client: '' }));
          }}>
            <option value="">— select existing client —</option>
            {clients.map(c => <option key={c.id} value={c.id}>{c.company ? `${c.company} · ` : ''}{c.name}</option>)}
            <option value="__new__">+ Add new client</option>
          </select>
          {fieldErrors.client && <div className="adm-field-error">{fieldErrors.client}</div>}
        </div>

        {isNewClient && (<>
          <div className="ct-field ct-field-wide">
            <label className="ct-label">Company Name *</label>
            <input className={`adm-input${fieldErrors.company ? ' adm-input--error' : ''}`} value={newClient.company}
              onChange={e => { setNewClient(n => ({ ...n, company: e.target.value })); setFieldErrors(er => ({ ...er, company: '' })); }}
              placeholder="Acme Corp GmbH" />
            {fieldErrors.company && <div className="adm-field-error">{fieldErrors.company}</div>}
          </div>
          <div className="ct-field ct-field-wide">
            <label className="ct-label">Contact Person *</label>
            <input className={`adm-input${fieldErrors.contactName ? ' adm-input--error' : ''}`} value={newClient.name}
              onChange={e => { setNewClient(n => ({ ...n, name: e.target.value })); setFieldErrors(er => ({ ...er, contactName: '' })); }}
              placeholder="John Doe" />
            {fieldErrors.contactName && <div className="adm-field-error">{fieldErrors.contactName}</div>}
          </div>
          <div className="ct-field">
            <label className="ct-label">Email</label>
            <input className={`adm-input${fieldErrors.email ? ' adm-input--error' : ''}`} type="email" value={newClient.email}
              onChange={e => { setNewClient(n => ({ ...n, email: e.target.value })); setFieldErrors(er => ({ ...er, email: '' })); }}
              placeholder="john@acme.com" />
            {fieldErrors.email && <div className="adm-field-error">{fieldErrors.email}</div>}
          </div>
          <div className="ct-field">
            <label className="ct-label">Phone</label>
            <input className="adm-input" type="tel" value={newClient.phone}
              onChange={e => setNewClient(n => ({ ...n, phone: e.target.value }))} placeholder="+49 123 456789" />
          </div>
        </>)}

        {/* MSA — optional, only shown for 3+ months */}
        {longTerm && !isNewClient && clientId && (
          <div className="ct-field ct-field-wide">
            <label className="ct-label">Link to MSA <span style={{ color: '#888', fontSize: 11 }}>— optional</span></label>
            {clientMSAs.length === 0
              ? <div style={{ padding: '10px 14px', background: 'rgba(243,156,18,0.08)', border: '1px solid rgba(243,156,18,0.25)', borderRadius: 8, fontSize: 13, color: '#f39c12' }}>
                  No MSAs for this client yet — you can add one in the MSA section.
                </div>
              : <select className="adm-input" value={msaId} onChange={e => setMsaId(e.target.value)}>
                  <option value="">— no MSA —</option>
                  {clientMSAs.map(m => <option key={m.id} value={m.id}>{m.msa_number}</option>)}
                </select>
            }
          </div>
        )}
        {longTerm && isNewClient && (
          <div className="ct-field ct-field-wide">
            <div style={{ padding: '10px 14px', background: 'rgba(110,252,245,0.06)', border: '1px solid rgba(110,252,245,0.18)', borderRadius: 8, fontSize: 13, color: 'var(--adm-cyan)' }}>
              New client will be created. You can generate an MSA for them from the MSA section afterwards.
            </div>
          </div>
        )}

        {/* SOW Number */}
        <div className="ct-field">
          <label className="ct-label">SOW Number <span style={{ color: '#888', fontSize: 11 }}>— editable</span></label>
          <input className="adm-input" value={sowNumber} onChange={e => setSowNumber(e.target.value)} placeholder="e.g. 7EITS022" style={{ fontFamily: 'monospace' }} />
        </div>

        {/* Project Name */}
        <div className="ct-field ct-field-wide">
          <label className="ct-label">Project Name *</label>
          <input className={`adm-input${fieldErrors.projectName ? ' adm-input--error' : ''}`} value={projectName}
            onChange={e => { setProjectName(e.target.value); setFieldErrors(er => ({ ...er, projectName: '' })); }}
            placeholder="e.g. Phase 2 – Backend Integration" />
          {fieldErrors.projectName && <div className="adm-field-error">{fieldErrors.projectName}</div>}
        </div>

        {/* Costs */}
        <div className="ct-field">
          <label className="ct-label">Fixed Project Price (€)</label>
          <input className={`adm-input${fieldErrors.projectCost ? ' adm-input--error' : ''}`} type="number" min="0" step="0.01" value={projectCost}
            onChange={e => { setProjectCost(e.target.value); setFieldErrors(er => ({ ...er, projectCost: '' })); }}
            placeholder="e.g. 12500.00" />
          {fieldErrors.projectCost && <div className="adm-field-error">{fieldErrors.projectCost}</div>}
        </div>
        <div className="ct-field">
          <label className="ct-label">Hosting Cost / Year (€)</label>
          <input className={`adm-input${fieldErrors.hostingCost ? ' adm-input--error' : ''}`} type="number" min="0" step="0.01" value={hostingCost}
            onChange={e => { setHostingCost(e.target.value); setFieldErrors(er => ({ ...er, hostingCost: '' })); }}
            placeholder="e.g. 600.00" />
          {fieldErrors.hostingCost && <div className="adm-field-error">{fieldErrors.hostingCost}</div>}
        </div>

        {/* Upload signed SOW */}
        <div className="ct-field ct-field-wide">
          <label className="ct-label">Upload Signed SOW <span style={{ color: '#888', fontSize: 11 }}>— optional</span></label>
          <input className="adm-input" type="file" accept=".pdf,.doc,.docx,.png,.jpg,.jpeg,.gif,.webp,.bmp,.tiff"
            onChange={e => setSignedFile(e.target.files[0] || null)} style={{ padding: '8px 12px', cursor: 'pointer' }} />
        </div>
      </div>

      {error && <p style={{ color: '#fc0071', fontSize: 14, margin: '8px 0' }}>{error}</p>}
      <div className="ct-form-actions" style={{ marginTop: 16 }}>
        <button className="adm-btn adm-btn-ghost adm-btn-sm" onClick={onCancel}>Cancel</button>
        <button className="adm-btn adm-btn-sm" disabled={submitting} onClick={submit}>
          {submitting ? 'Creating…' : 'Generate SOW'}
        </button>
      </div>
    </div>
  );
}

function SowModal({ sow, msas, clients = [], token, onUpdate, onClose, onDelete }) {
  const [editing, setEditing] = useState(false);
  const [saving,  setSaving]  = useState(false);
  const [err,     setErr]     = useState('');
  const [form, setForm] = useState({});

  const startEdit = () => {
    setForm({
      client_id:       sow.client_id      != null ? String(sow.client_id)        : '',
      sow_number:      sow.sow_number     || '',
      project_name:    sow.project_name   || '',
      duration_months: String(sow.duration_months >= 3 ? 3 : 1),
      service_tier:    sow.service_tier   || '',
      cost_of_project: sow.cost_of_project != null ? String(sow.cost_of_project) : '',
      cost_of_hosting: sow.cost_of_hosting != null ? String(sow.cost_of_hosting) : '',
      msa_id:          sow.msa_id         != null ? String(sow.msa_id)           : '',
    });
    setErr('');
    setEditing(true);
  };

  const [formFieldErrors, setFormFieldErrors] = useState({});

  const save = async () => {
    const ferrs = {};
    if (!form.project_name.trim()) ferrs.project_name = 'Project name is required';
    if (form.cost_of_project !== '' && (isNaN(parseFloat(form.cost_of_project)) || parseFloat(form.cost_of_project) < 0))
      ferrs.cost_of_project = 'Enter a valid amount (0 or more)';
    if (form.cost_of_hosting !== '' && (isNaN(parseFloat(form.cost_of_hosting)) || parseFloat(form.cost_of_hosting) < 0))
      ferrs.cost_of_hosting = 'Enter a valid amount (0 or more)';
    if (Object.keys(ferrs).length) { setFormFieldErrors(ferrs); return; }
    setFormFieldErrors({});
    setSaving(true); setErr('');
    try {
      const res = await fetch(`/api/admin/sows/${sow.id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', 'x-admin-token': token },
        body: JSON.stringify({
          client_id:       form.client_id       !== '' ? form.client_id       : undefined,
          sow_number:      form.sow_number.trim() || undefined,
          project_name:    form.project_name.trim(),
          duration_months: parseInt(form.duration_months, 10),
          service_tier:    form.service_tier   || null,
          cost_of_project: form.cost_of_project !== '' ? form.cost_of_project : null,
          cost_of_hosting: form.cost_of_hosting !== '' ? form.cost_of_hosting : null,
          msa_id:          form.msa_id          !== '' ? form.msa_id          : null,
        }),
      });
      if (!res.ok) throw new Error((await res.json()).error || 'Save failed');
      onUpdate(await res.json());
      setEditing(false);
    } catch (e) { setErr(e.message); }
    finally { setSaving(false); }
  };

  const fmt = n => Number(n||0).toLocaleString('de-DE', { minimumFractionDigits: 0 });
  const tierObj = SOW_TIERS.find(x => x.key === sow.service_tier);

  return (
    <div className="sow-modal-overlay" onClick={onClose}>
      <div className="sow-modal" onClick={e => e.stopPropagation()}>

        {/* Header */}
        <div className="sow-modal-header">
          <div className="sow-modal-title-row">
            <span className="sow-modal-num">{sow.sow_number}</span>
            <CcBadge status={sow.status || 'draft'} />
            {tierObj && (
              <span className="sow-modal-tier-badge" style={{ background: `${tierObj.color}22`, color: tierObj.color }}>
                {tierObj.icon} {tierObj.label} — {tierObj.name}
              </span>
            )}
          </div>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            {!editing && (
              <button className="adm-btn adm-btn-ghost adm-btn-sm" onClick={startEdit}>✏ Edit</button>
            )}
            <button className="sow-modal-close" onClick={onClose}>✕</button>
          </div>
        </div>

        {/* Body */}
        <div className="sow-modal-body">
          {err && <div className="adm-error" style={{ marginBottom: 14 }}>{err}</div>}

          {editing ? (
            <div className="sow-modal-edit-grid">
              <div className="sow-modal-edit-field">
                <label className="sow-modal-meta-label">SOW Number</label>
                <input className="adm-input sow-modal-input" value={form.sow_number}
                  onChange={e => setForm(f => ({ ...f, sow_number: e.target.value }))}
                  placeholder={sow.sow_number} style={{ fontFamily: 'monospace' }} />
              </div>
              <div className="sow-modal-edit-field">
                <label className="sow-modal-meta-label">Project Name</label>
                <input className={`adm-input sow-modal-input${formFieldErrors.project_name ? ' adm-input--error' : ''}`} value={form.project_name}
                  onChange={e => { setForm(f => ({ ...f, project_name: e.target.value })); setFormFieldErrors(fe => ({ ...fe, project_name: '' })); }} />
                {formFieldErrors.project_name && <div className="adm-field-error">{formFieldErrors.project_name}</div>}
              </div>
              <div className="sow-modal-edit-field">
                <label className="sow-modal-meta-label">Duration</label>
                <select className="adm-input sow-modal-input" value={form.duration_months}
                  onChange={e => setForm(f => ({ ...f, duration_months: e.target.value }))}>
                  <option value="1">Short-term (&lt; 3 months)</option>
                  <option value="3">Long-term (3+ months)</option>
                </select>
              </div>
              <div className="sow-modal-edit-field">
                <label className="sow-modal-meta-label">Service Tier</label>
                <select className="adm-input sow-modal-input" value={form.service_tier}
                  onChange={e => setForm(f => ({ ...f, service_tier: e.target.value }))}>
                  <option value="">— No tier —</option>
                  {SOW_TIERS.map(t => <option key={t.key} value={t.key}>{t.icon} {t.label} — {t.name}</option>)}
                </select>
              </div>
              <div className="sow-modal-edit-field">
                <label className="sow-modal-meta-label">MSA Link</label>
                <select className="adm-input sow-modal-input" value={form.msa_id}
                  onChange={e => setForm(f => ({ ...f, msa_id: e.target.value }))}>
                  <option value="">— None —</option>
                  {msas.filter(m => !form.client_id || String(m.client_id) === String(form.client_id)).map(m => <option key={m.id} value={m.id}>{m.msa_number}</option>)}
                </select>
              </div>
              <div className="sow-modal-edit-field">
                <label className="sow-modal-meta-label">Fixed Price (€)</label>
                <input className={`adm-input sow-modal-input${formFieldErrors.cost_of_project ? ' adm-input--error' : ''}`} type="number" min="0" placeholder="0"
                  value={form.cost_of_project}
                  onChange={e => { setForm(f => ({ ...f, cost_of_project: e.target.value })); setFormFieldErrors(fe => ({ ...fe, cost_of_project: '' })); }} />
                {formFieldErrors.cost_of_project && <div className="adm-field-error">{formFieldErrors.cost_of_project}</div>}
              </div>
              <div className="sow-modal-edit-field">
                <label className="sow-modal-meta-label">Hosting / Year (€)</label>
                <input className={`adm-input sow-modal-input${formFieldErrors.cost_of_hosting ? ' adm-input--error' : ''}`} type="number" min="0" placeholder="0"
                  value={form.cost_of_hosting}
                  onChange={e => { setForm(f => ({ ...f, cost_of_hosting: e.target.value })); setFormFieldErrors(fe => ({ ...fe, cost_of_hosting: '' })); }} />
                {formFieldErrors.cost_of_hosting && <div className="adm-field-error">{formFieldErrors.cost_of_hosting}</div>}
              </div>
              <div className="sow-modal-edit-field sow-modal-edit-wide">
                <label className="sow-modal-meta-label">Client</label>
                <select className="adm-input sow-modal-input" value={form.client_id}
                  onChange={e => setForm(f => ({ ...f, client_id: e.target.value, msa_id: '' }))}>
                  <option value="">— select client —</option>
                  {clients.map(c => <option key={c.id} value={c.id}>{c.company ? `${c.company} · ` : ''}{c.name}</option>)}
                </select>
              </div>
            </div>
          ) : (
            <div className="sow-modal-meta">
              <div className="sow-modal-meta-item">
                <span className="sow-modal-meta-label">Client</span>
                <span className="sow-modal-meta-val">{sow.client_name || '—'}</span>
              </div>
              <div className="sow-modal-meta-item">
                <span className="sow-modal-meta-label">Company</span>
                <span className="sow-modal-meta-val">{sow.company || '—'}</span>
              </div>
              <div className="sow-modal-meta-item">
                <span className="sow-modal-meta-label">Project</span>
                <span className="sow-modal-meta-val">{sow.project_name || '—'}</span>
              </div>
              <div className="sow-modal-meta-item">
                <span className="sow-modal-meta-label">Duration</span>
                <span className="sow-modal-meta-val">{sow.duration_months >= 3 ? 'Long-term (3+ months)' : 'Short-term (< 3 months)'}</span>
              </div>
              <div className="sow-modal-meta-item">
                <span className="sow-modal-meta-label">MSA</span>
                <span className="sow-modal-meta-val" style={{ fontFamily: 'monospace', color: 'var(--adm-cyan)' }}>{sow.msa_number || '—'}</span>
              </div>
              <div className="sow-modal-meta-item">
                <span className="sow-modal-meta-label">Fixed Price</span>
                <span className="sow-modal-meta-val" style={{ color: '#6efcf5', fontWeight: 700 }}>
                  {sow.cost_of_project != null ? `€${fmt(sow.cost_of_project)}` : '—'}
                </span>
              </div>
              <div className="sow-modal-meta-item">
                <span className="sow-modal-meta-label">Hosting / Year</span>
                <span className="sow-modal-meta-val" style={{ color: '#a78bfa', fontWeight: 700 }}>
                  {sow.cost_of_hosting != null ? `€${fmt(sow.cost_of_hosting)}` : '—'}
                </span>
              </div>
              <div className="sow-modal-meta-item">
                <span className="sow-modal-meta-label">Created</span>
                <span className="sow-modal-meta-val">{ctFmt(sow.created_at)}</span>
              </div>
              <div className="sow-modal-meta-item">
                <span className="sow-modal-meta-label">Sent</span>
                <span className="sow-modal-meta-val">{ctFmt(sow.sent_at)}</span>
              </div>
            </div>
          )}

          <div className="sow-modal-steps-title" style={{ marginTop: 24 }}>Document Steps</div>
          <CcDocSteps doc={sow} docType="sow" token={token} onUpdate={onUpdate} />
        </div>

        {/* Footer */}
        <div className="sow-modal-footer">
          <button className="adm-btn adm-btn-danger adm-btn-sm" onClick={onDelete}>Delete SOW</button>
          <div style={{ display: 'flex', gap: 8, marginLeft: 'auto' }}>
            {editing ? (
              <>
                <button className="adm-btn adm-btn-ghost adm-btn-sm" onClick={() => setEditing(false)} disabled={saving}>Cancel</button>
                <button className="adm-btn adm-btn-sm" onClick={save} disabled={saving}>
                  {saving ? <><span className="adm-spinner" style={{ width: 12, height: 12, borderWidth: 2 }} /> Saving…</> : 'Save Changes'}
                </button>
              </>
            ) : (
              <button className="adm-btn adm-btn-ghost adm-btn-sm" onClick={onClose}>Done</button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function ContractsSOW({ token }) {
  const [sows, setSows] = useState([]);
  const [clients, setClients] = useState([]);
  const [msas, setMsas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showWizard, setShowWizard] = useState(false);
  const [selectedSOW, setSelectedSOW] = useState(null);  // edit modal
  const [expandedId, setExpandedId] = useState(null);     // inline expand
  const [confirmDel, setConfirmDel] = useState(null);
  const [toast, setToast] = useState(null);
  const H = { 'x-admin-token': token };
  const showToast = (msg, isErr = false) => { setToast({ msg, isErr }); setTimeout(() => setToast(null), 3200); };

  const loadAll = useCallback(async () => {
    try {
      const [cr, mr, sr] = await Promise.all([
        fetch('/api/admin/clients', { headers: H }).then(r => r.json()),
        fetch('/api/admin/msas',    { headers: H }).then(r => r.json()),
        fetch('/api/admin/sows',    { headers: H }).then(r => r.json()),
      ]);
      setClients(Array.isArray(cr) ? cr : []);
      setMsas(Array.isArray(mr) ? mr : []);
      setSows(Array.isArray(sr) ? sr : []);
    } catch { showToast('Failed to load', true); }
    finally { setLoading(false); }
  }, [token]);

  useEffect(() => { loadAll(); }, [loadAll]);

  const handleCreated = (sow) => {
    setSows(ss => [sow, ...ss]);
    setShowWizard(false);
    showToast(`SOW ${sow.sow_number} created`);
    fetch('/api/admin/clients', { headers: H }).then(r => r.json()).then(cr => { if (Array.isArray(cr)) setClients(cr); });
  };

  const deleteSOW = async () => {
    const res = await fetch(`/api/admin/sows/${confirmDel}`, { method: 'DELETE', headers: H });
    if (res.ok) { setSows(ss => ss.filter(s => s.id !== confirmDel)); showToast('Deleted'); }
    else showToast('Delete failed', true);
    setConfirmDel(null);
  };

  const updateSOW = (s) => {
    setSows(ss => ss.map(x => x.id === s.id ? s : x));
    setSelectedSOW(prev => prev?.id === s.id ? s : prev);
  };

  if (loading) return <div className="adm-empty">Loading\u2026</div>;

  return (
    <div>
      <div className="ct-header">
        <div>
          <h2 className="adm-page-title">Statements of Work</h2>
          <p className="adm-page-sub">Project contracts — short-term and long-term</p>
        </div>
        {!showWizard && <button className="adm-btn adm-btn-sm" onClick={() => setShowWizard(true)}>+ New SOW</button>}
      </div>

      <div className="adm-stats" style={{ marginBottom: 20 }}>
        <div className="adm-stat-card"><div className="adm-stat-label">Total SOWs</div><div className="adm-stat-value">{sows.length}</div></div>
        <div className="adm-stat-card"><div className="adm-stat-label">Short-term</div><div className="adm-stat-value">{sows.filter(s => s.duration_months < 3).length}</div></div>
        <div className="adm-stat-card"><div className="adm-stat-label">Long-term</div><div className="adm-stat-value">{sows.filter(s => s.duration_months >= 3).length}</div></div>
        <div className="adm-stat-card"><div className="adm-stat-label">Sent</div><div className="adm-stat-value">{sows.filter(s => s.sent_at).length}</div></div>
      </div>

      {(() => {
        const fmt = n => Number(n||0).toLocaleString('de-DE', { minimumFractionDigits: 0, maximumFractionDigits: 0 });
        const tiers = [
          { key: 'tier1', label: 'Tier 1', color: '#6efcf5', targetUnits: 12, targetRev: 30000 },
          { key: 'tier2', label: 'Tier 2', color: '#a78bfa', targetUnits: 4,  targetRev: 30000 },
          { key: 'tier3', label: 'Tier 3', color: '#f59e0b', targetUnits: 1,  targetRev: 22000 },
          { key: 'hosting', label: 'Hosting', color: '#fc0071', targetUnits: 12, targetRev: 3000 },
        ];
        const stats = tiers.map(t => {
          if (t.key === 'hosting') {
            const withHosting = sows.filter(s => s.cost_of_hosting > 0);
            return { ...t, count: withHosting.length, rev: withHosting.reduce((s, x) => s + parseFloat(x.cost_of_hosting || 0), 0) };
          }
          const matched = sows.filter(s => s.service_tier === t.key);
          return { ...t, count: matched.length, rev: matched.reduce((s, x) => s + parseFloat(x.cost_of_project || 0), 0) };
        });
        return (
          <div className="sow-tier-progress" style={{ marginBottom: 24 }}>
            {stats.map(t => {
              const uPct = Math.min(100, Math.round((t.count / t.targetUnits) * 100));
              const rPct = Math.min(100, Math.round((t.rev   / t.targetRev)   * 100));
              return (
                <div key={t.key} className="sow-tier-row">
                  <div className="sow-tier-name" style={{ color: t.color }}>{t.label}</div>
                  <div className="sow-tier-stat">
                    <span className="sow-tier-label">Achieved</span>
                    <span className="sow-tier-val">{t.count}<span className="sow-tier-of">/{t.targetUnits}</span></span>
                    <div className="sow-tier-bar"><div className="sow-tier-fill" style={{ width: `${uPct}%`, background: t.color }} /></div>
                  </div>
                  <div className="sow-tier-stat">
                    <span className="sow-tier-label">Value (€)</span>
                    <span className="sow-tier-val">€{fmt(t.rev)}<span className="sow-tier-of">/€{fmt(t.targetRev)}</span></span>
                    <div className="sow-tier-bar"><div className="sow-tier-fill" style={{ width: `${rPct}%`, background: t.color, opacity: 0.7 }} /></div>
                  </div>
                  <div className="sow-tier-pct" style={{ color: t.color }}>{uPct}%</div>
                </div>
              );
            })}
          </div>
        );
      })()}

      {showWizard && <SowWizard clients={clients} msas={msas} token={token} onCreated={handleCreated} onCancel={() => setShowWizard(false)} />}

      {sows.length === 0 && !showWizard
        ? <div className="adm-empty">No SOWs yet. Click "+ New SOW" to create one.</div>
        : <div className="ct-list">
            {sows.map(sow => {
              const isOpen = expandedId === sow.id;
              const isLong = sow.duration_months >= 3;
              const tierObj = SOW_TIERS.find(x => x.key === sow.service_tier);
              const stepIdx = sow.completed_at ? 4 : !sow.draft_file ? 0 : !sow.final_file ? 1 : !sow.sent_at ? 2 : 3;
              const stepLabel = ['Upload Draft', 'Upload Final', 'Email to Client', 'Mark Complete', 'Complete'][stepIdx];
              const stepColor = stepIdx === 4 ? '#3bdfb5' : '#f39c12';
              return (
                <div key={sow.id} className={`ct-row${isOpen ? ' ct-row-open' : ''}`}>
                  <div className="ct-row-main" onClick={() => setExpandedId(isOpen ? null : sow.id)}>
                    <div className="ct-row-left">
                      <div className="cns-step-dot" style={{ background: stepColor, boxShadow: `0 0 8px ${stepColor}40`, fontSize: 12 }}>{stepIdx < 4 ? stepIdx + 1 : '✓'}</div>
                      <div className="ct-cns-info">
                        <div className="ct-title" style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                          <span style={{ fontFamily: 'monospace', fontSize: 14 }}>{sow.sow_number}</span>
                          <span style={{ fontSize: 14, fontWeight: 500, color: 'var(--adm-text)' }}>{sow.project_name}</span>
                          {tierObj && <span style={{ fontSize: 11, fontWeight: 600, background: `${tierObj.color}22`, color: tierObj.color, padding: '1px 7px', borderRadius: 99 }}>{tierObj.label}</span>}
                          {isLong && <span className="cc-dur-tag cc-dur-tag-long">3+ mo</span>}
                        </div>
                        <div className="ct-cns-meta">
                          <span className="ct-cns-email">{sow.company || sow.client_name}</span>
                          {sow.msa_number && <span className="ct-cns-nda">{sow.msa_number}</span>}
                          {sow.cost_of_project != null && <span style={{ fontSize: 12, color: '#6efcf5', fontWeight: 600 }}>€{Number(sow.cost_of_project).toLocaleString('de-DE', { minimumFractionDigits: 0 })}</span>}
                          {sow.cost_of_hosting != null && <span style={{ fontSize: 12, color: '#a78bfa', fontWeight: 600 }}>€{Number(sow.cost_of_hosting).toLocaleString('de-DE', { minimumFractionDigits: 0 })}/yr</span>}
                        </div>
                      </div>
                    </div>
                    <div className="ct-row-right">
                      <button className="adm-btn adm-btn-ghost adm-btn-sm" style={{ fontSize: 13 }}
                        onClick={e => { e.stopPropagation(); setSelectedSOW(sow); setExpandedId(null); }}>✏️ Edit</button>
                      <span style={{ fontSize: 13, fontWeight: 600, letterSpacing: 1, textTransform: 'uppercase',
                        color: stepColor, background: `${stepColor}1a`, padding: '3px 10px', borderRadius: 99 }}>
                        {stepLabel}
                      </span>
                      <span className="ct-ts">{ctFmt(sow.created_at)}</span>
                      <span className="ct-chevron">{isOpen ? '▲' : '▼'}</span>
                    </div>
                  </div>

                  {/* Mini progress strip */}
                  <div className="cns-prog-mini-wrap">
                    <div className="cns-prog-mini-fill" style={{ width: `${[0, 25, 50, 75, 100][stepIdx]}%`, background: stepColor }} />
                  </div>

                  {isOpen && (
                    <div className="ct-detail" onClick={e => e.stopPropagation()}>
                      <CcDocSteps doc={sow} docType="sow" token={token} onUpdate={updateSOW} />
                      <div className="ct-detail-actions">
                        <button className="adm-btn adm-btn-ghost adm-btn-sm" onClick={() => { setSelectedSOW(sow); setExpandedId(null); }}>✏️ Edit Details</button>
                        <button className="adm-btn adm-btn-danger adm-btn-sm" onClick={() => setConfirmDel(sow.id)}>Delete SOW</button>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
      }

      {selectedSOW && (
        <SowModal
          sow={selectedSOW}
          msas={msas}
          clients={clients}
          token={token}
          onUpdate={updateSOW}
          onClose={() => setSelectedSOW(null)}
          onDelete={() => { setConfirmDel(selectedSOW.id); setSelectedSOW(null); }}
        />
      )}

      {confirmDel && (
        <div className="adm-confirm-overlay">
          <div className="adm-confirm-box">
            <p className="adm-confirm-title">Delete SOW?</p>
            <p className="adm-confirm-text">This cannot be undone.</p>
            <div className="adm-confirm-actions">
              <button className="adm-btn adm-btn-ghost adm-btn-sm" onClick={() => setConfirmDel(null)}>Cancel</button>
              <button className="adm-btn adm-btn-danger adm-btn-sm" onClick={deleteSOW}>Delete</button>
            </div>
          </div>
        </div>
      )}
      {toast && <div className={`adm-toast${toast.isErr ? ' adm-toast-err' : ''}`}>{toast.msg}</div>}
    </div>
  );
}

// ── IT Consultant Onboarding ──
// CONSULTANTS_KEY is kept for localStorage backwards-compat but the live data
// comes from the server — local storage is only a stale-state fallback.
const CONSULTANTS_KEY = 'consultants_v1';

const CNS_STATUS = {
  invited:           { label: 'Email Sent',        color: '#a78bfa', step: 1 },
  submitted:         { label: 'NDA Pending',        color: '#f39c12', step: 2 },
  nda_ready:         { label: 'NDA Pending',        color: '#6efcf5', step: 2 },
  consultant_signed: { label: 'Signed & Uploaded',  color: '#3bdfb5', step: 3 },
  counter_signed:    { label: 'Complete',            color: '#fc0071', step: 5 },
};

// 16 random bytes → 32-char hex string used as the one-time onboarding link token.
// crypto.getRandomValues is the browser CSPRNG — safe for security-sensitive tokens.
function cnsGenToken() {
  return Array.from(crypto.getRandomValues(new Uint8Array(16)))
    .map(b => b.toString(16).padStart(2, '0')).join('');
}

function cnsGenNDANumber(list) {
  const y = new Date().getFullYear();
  const n = list.filter(c => (c.nda_number || c.ndaNumber || '').includes(`-${y}-`)).length + 1;
  return `7EITS-NDA-${y}-${String(n).padStart(3, '0')}`;
}


const CNS_STEPS = [
  { label: 'Email Sent',        statuses: ['invited'] },
  { label: 'NDA Pending',       statuses: ['submitted', 'nda_ready'] },
  { label: 'Signed & Uploaded', statuses: ['consultant_signed'] },
  { label: 'Counter-Signed',    statuses: [] },
  { label: 'Complete',          statuses: ['counter_signed'] },
];

function statusToStepIndex(status) {
  if (status === 'invited')           return 0;
  if (status === 'submitted' || status === 'nda_ready') return 1;
  if (status === 'consultant_signed') return 2;
  if (status === 'counter_signed')    return 4;
  return 0;
}

function CnsProgressBar({ status }) {
  const active = statusToStepIndex(status);
  const pct = [20, 40, 60, 80, 100][active];
  return (
    <div className="cns-prog-wrap">
      <div className="cns-prog-track">
        <div className="cns-prog-fill" style={{ width: `${pct}%` }} />
      </div>
      <div className="cns-prog-steps">
        {CNS_STEPS.map((s, i) => (
          <div key={i} className={`cns-prog-step${i < active ? ' done' : i === active ? ' active' : ''}`}>
            <div className="cns-prog-dot">{i < active ? '✓' : i + 1}</div>
            <div className="cns-prog-label">{s.label}</div>
          </div>
        ))}
      </div>
      <div className="cns-prog-pct">{pct}%</div>
    </div>
  );
}

const CNS_EMPTY_DETAILS = { name: '', mobile: '', address: '', bankName: '', bankBranch: '', accountHolder: '', iban: '', swift: '', upiId: '' };

function CnsDetailForm({ initial, onSave, onCancel, saving }) {
  const [form, setForm] = useState(initial || CNS_EMPTY_DETAILS);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const valid = form.name.trim() && form.address.trim() && form.iban.trim();
  return (
    <div>
      <div className="ct-form-grid">
        <div className="ct-field ct-field-wide">
          <label className="ct-label">Full Legal Name *</label>
          <input className="adm-input" value={form.name} onChange={e => set('name', e.target.value)} placeholder="As on ID / passport" autoFocus />
        </div>
        <div className="ct-field">
          <label className="ct-label">Mobile Number</label>
          <input className="adm-input" type="tel" value={form.mobile || ''} onChange={e => set('mobile', e.target.value)} placeholder="+49 123 456 7890" />
        </div>
        <div className="ct-field ct-field-wide">
          <label className="ct-label">Full Address *</label>
          <textarea className="adm-input ct-notes" rows={3} value={form.address} onChange={e => set('address', e.target.value)} placeholder={'Street & Number\nCity, Postal Code\nCountry'} />
        </div>
        <div className="ct-field">
          <label className="ct-label">Bank Name</label>
          <input className="adm-input" value={form.bankName} onChange={e => set('bankName', e.target.value)} placeholder="e.g. Deutsche Bank" />
        </div>
        <div className="ct-field">
          <label className="ct-label">Bank Branch</label>
          <input className="adm-input" value={form.bankBranch} onChange={e => set('bankBranch', e.target.value)} placeholder="e.g. Main Branch, Sector 18" />
        </div>
        <div className="ct-field">
          <label className="ct-label">Account Holder Name</label>
          <input className="adm-input" value={form.accountHolder} onChange={e => set('accountHolder', e.target.value)} placeholder="As on bank account" />
        </div>
        <div className="ct-field ct-field-wide">
          <label className="ct-label">IBAN / Account Number *</label>
          <input className="adm-input" style={{ fontFamily: 'monospace' }} value={form.iban} onChange={e => set('iban', e.target.value.toUpperCase().replace(/\s/g, ''))} placeholder="DE89370400440532013000" />
        </div>
        <div className="ct-field">
          <label className="ct-label">SWIFT / BIC / IFSC</label>
          <input className="adm-input" style={{ fontFamily: 'monospace' }} value={form.swift} onChange={e => set('swift', e.target.value.toUpperCase())} placeholder="DEUTDEDB" />
        </div>
        <div className="ct-field">
          <label className="ct-label">UPI ID</label>
          <input className="adm-input" style={{ fontFamily: 'monospace' }} value={form.upiId} onChange={e => set('upiId', e.target.value)} placeholder="name@upi" />
        </div>
      </div>
      <div className="ct-form-actions">
        <button className="adm-btn adm-btn-ghost adm-btn-sm" onClick={onCancel} disabled={saving}>Cancel</button>
        <button className="adm-btn adm-btn-sm" disabled={!valid || saving} onClick={() => onSave(form)}>
          {saving ? <span className="adm-spinner" /> : 'Save Details'}
        </button>
      </div>
    </div>
  );
}

function Consultants({ token: adminToken }) {
  const [list, setList]           = useState([]);
  const [loading, setLoading]     = useState(true);
  const [email, setEmail]         = useState('');
  const [copied, setCopied]       = useState('');
  const [expandedId, setExpandedId] = useState(null);
  const [confirmDel, setConfirmDel] = useState(null);
  const [confirmBulkDel, setConfirmBulkDel] = useState(false);
  const [filter, setFilter]       = useState('all');
  const [search, setSearch]       = useState('');
  const [toast, setToast]         = useState('');
  const [fillModal, setFillModal] = useState(null);
  const [fillSaving, setFillSaving] = useState(false);

  const headers = { 'x-admin-token': adminToken, 'Content-Type': 'application/json' };

  const showToast = (msg) => { setToast(msg); setTimeout(() => setToast(''), 3500); };

  const load = useCallback(async () => {
    try {
      const res = await fetch(`${API}/admin/consultants`, { headers: { 'x-admin-token': adminToken } });
      const data = await res.json();
      setList(Array.isArray(data) ? data : []);
    } catch { setList([]); }
    finally { setLoading(false); }
  }, [adminToken]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { load(); }, [load]);

  const invite = async () => {
    if (!email.trim()) { showToast('Please enter an email address.'); return; }
    if (!validEmail(email)) { showToast('Please enter a valid email address.'); return; }
    const tok = cnsGenToken();
    const link = `${window.location.origin}/onboarding/${tok}`;
    try {
      const res  = await fetch(`${API}/admin/consultants`, { method: 'POST', headers, body: JSON.stringify({ email: email.trim(), token: tok, link }) });
      const data = await res.json();
      setEmail('');
      if (data.exists) {
        setExpandedId(data.consultant.id);
        showToast(`${email.trim()} already exists — profile opened below.`);
        return;
      }
      load();
      showToast('Invitation created and email sent to consultant.');
    } catch { showToast('Failed to create invitation.'); }
  };

  const fillDetails = async (form) => {
    if (!fillModal) return;
    setFillSaving(true);
    try {
      await fetch(`${API}/admin/consultants/${fillModal.id}/details`, {
        method: 'PATCH', headers,
        body: JSON.stringify({ name: form.name, mobile: form.mobile, address: form.address, bankName: form.bankName, bankBranch: form.bankBranch, accountHolder: form.accountHolder, iban: form.iban, swift: form.swift, upiId: form.upiId }),
      });
      setFillModal(null);
      load();
      showToast('Consultant details saved. Ready to generate NDA.');
    } catch { showToast('Failed to save details.'); }
    finally { setFillSaving(false); }
  };

  const copyLink = (tok) => {
    const url = `${window.location.origin}/onboarding/${tok}`;
    navigator.clipboard.writeText(url);
    setCopied(tok);
    setTimeout(() => setCopied(''), 2500);
  };

  const fetchAndOpenNDA = async (id, download = false) => {
    try {
      const res = await fetch(`${API}/admin/consultants/${id}/nda`, { headers: { 'x-admin-token': adminToken } });
      if (!res.ok) { showToast('NDA not available yet.'); return; }
      const html = await res.text();
      if (download) {
        const blob = new Blob([html], { type: 'text/html' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url; a.download = `NDA_${id}.html`;
        document.body.appendChild(a); a.click();
        document.body.removeChild(a); URL.revokeObjectURL(url);
        return;
      }
      const w = window.open('', '_blank');
      if (!w) { showToast('Popup blocked — please allow popups and try again.'); return; }
      w.document.write(html);
      w.document.close();
    } catch { showToast('Failed to load NDA.'); }
  };

  const generateNDA = async (c) => {
    const ndaNumber = cnsGenNDANumber(list);
    try {
      await fetch(`${API}/admin/consultants/${c.id}/nda`, { method: 'PATCH', headers, body: JSON.stringify({ ndaNumber }) });
      await fetchAndOpenNDA(c.id);
      load();
    } catch { showToast('Failed to update status.'); }
  };

  const resendNda = async (id) => {
    try {
      await fetch(`${API}/admin/consultants/${id}/resend-nda`, { method: 'POST', headers });
      showToast('NDA email sent to consultant.');
    } catch { showToast('Failed to send email.'); }
  };

  const downloadFile = async (filename) => {
    try {
      const res = await fetch(`${API}/admin/uploads/${encodeURIComponent(filename)}`, { headers: { 'x-admin-token': adminToken } });
      if (!res.ok) { showToast('File not found on server.'); return; }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename.replace(/^(?:cons_\d+_|\d+_)/, '');
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch { showToast('Download failed.'); }
  };

  const del = async (id) => {
    await fetch(`${API}/admin/consultants/${id}`, { method: 'DELETE', headers: { 'x-admin-token': adminToken } });
    setConfirmDel(null);
    if (expandedId === id) setExpandedId(null);
    load();
  };

  const filtered = list.filter(c => {
    if (filter !== 'all' && c.status !== filter) return false;
    if (!search.trim()) return true;
    const q = search.toLowerCase();
    return (
      (c.name || '').toLowerCase().includes(q) ||
      (c.email || '').toLowerCase().includes(q) ||
      (c.nda_number || '').toLowerCase().includes(q) ||
      (CNS_STATUS[c.status]?.label || c.status).toLowerCase().includes(q)
    );
  });

  const sel = useSelection(filtered);

  const bulkDeleteConsultants = async () => {
    await Promise.all([...sel.selected].map(id => fetch(`${API}/admin/consultants/${id}`, { method: 'DELETE', headers: { 'x-admin-token': adminToken } })));
    sel.clear();
    load();
  };

  return (
    <div>
      <div className="ct-header">
        <div>
          <h2 className="adm-page-title">IT Consultant Onboarding</h2>
          <p className="adm-page-sub">NDA generation and compliance workflow for IT consultants</p>
        </div>
      </div>

      {toast && <div className="adm-error" style={{ background: 'rgba(110,252,245,0.06)', borderColor: 'rgba(110,252,245,0.2)', color: 'var(--adm-cyan)', marginBottom: 20 }}>{toast}</div>}

      {/* Invite form */}
      <div className="cns-invite-card">
        <label className="ct-label">Invite IT Consultant</label>
        <div className="cns-invite-row">
          <input className="adm-input" type="email" value={email} onChange={e => setEmail(e.target.value)}
            placeholder="consultant@example.com" onKeyDown={e => e.key === 'Enter' && invite()} />
          <button className="adm-btn" onClick={invite} disabled={!email.trim() || !validEmail(email)}>Generate Link</button>
        </div>
        <p style={{ fontSize: 15, color: 'var(--adm-muted)', margin: '8px 0 0' }}>
          A unique, secure onboarding URL is generated. Share it with the consultant to collect their legal and banking details.
        </p>
      </div>

      {/* Pending Links panel */}
      {list.filter(c => c.status === 'invited').length > 0 && (
        <div className="cns-links-panel">
          <div className="cns-links-header">
            <span className="ct-label">Pending Onboarding Links</span>
            <span style={{ fontSize: 14, color: 'var(--adm-muted)' }}>
              {list.filter(c => c.status === 'invited').length} awaiting submission
            </span>
          </div>
          {list.filter(c => c.status === 'invited').map(c => {
            const link = `${window.location.origin}/onboarding/${c.token}`;
            return (
              <div key={c.id} className="cns-link-entry">
                <div className="cns-link-entry-left">
                  <span className="cns-link-email">{c.email}</span>
                  <span className="cns-link-url">{link}</span>
                </div>
                <div className="cns-link-entry-right">
                  <span className="ct-ts">{ctFmt(c.invited_at)}</span>
                  <button
                    className={`adm-btn adm-btn-sm${copied === c.token ? '' : ' adm-btn-ghost'}`}
                    onClick={() => copyLink(c.token)}
                  >
                    {copied === c.token ? '✓ Copied' : 'Copy Link'}
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Stats */}
      <div className="adm-stats" style={{ marginBottom: 24, gridTemplateColumns: 'repeat(5, 1fr)' }}>
        {Object.entries(CNS_STATUS).map(([k, v]) => (
          <div key={k} className="adm-stat-card" style={{ cursor: 'pointer', borderColor: filter === k ? v.color : undefined }}
            onClick={() => setFilter(f => f === k ? 'all' : k)}>
            <div className="adm-stat-label">{v.label}</div>
            <div className="adm-stat-value" style={{ fontSize: 26, color: v.color }}>
              {list.filter(c => c.status === k).length}
            </div>
          </div>
        ))}
      </div>

      {/* Search + Filter */}
      <div className="cns-search-row">
        <input
          className="adm-input cns-search-input"
          type="search"
          placeholder="Search by name, email, NDA number or status…"
          value={search}
          onChange={e => setSearch(e.target.value)}
        />
        {search && (
          <button className="cns-search-clear" onClick={() => setSearch('')} title="Clear search">✕</button>
        )}
      </div>
      <div className="adm-inbox-toolbar" style={{ marginBottom: sel.selected.size > 0 ? 8 : 16 }}>
        <input type="checkbox" className="adm-checkbox" checked={sel.allSelected} onChange={sel.toggleAll} title="Select all" />
        <button className={`adm-btn adm-btn-sm${filter === 'all' ? '' : ' adm-btn-ghost'}`} onClick={() => { setFilter('all'); sel.clear(); }}>All ({list.length})</button>
        {Object.entries(CNS_STATUS).map(([k, v]) => (
          <button key={k} className={`adm-btn adm-btn-sm${filter === k ? '' : ' adm-btn-ghost'}`}
            onClick={() => { setFilter(f => f === k ? 'all' : k); sel.clear(); }}>
            {v.label}
          </button>
        ))}
      </div>
      {sel.selected.size > 0 && (
        <div className="adm-bulk-bar" style={{ marginBottom: 16 }}>
          <span className="adm-bulk-count">{sel.selected.size} selected</span>
          <button className="adm-btn adm-btn-ghost adm-btn-sm" onClick={sel.clear}>Clear</button>
          <button className="adm-btn adm-btn-danger adm-btn-sm" onClick={() => setConfirmBulkDel(true)}>🗑 Delete ({sel.selected.size})</button>
        </div>
      )}

      {confirmBulkDel && (
        <div className="adm-confirm-overlay">
          <div className="adm-confirm-box">
            <p className="adm-confirm-title">Delete {sel.selected.size} consultant{sel.selected.size !== 1 ? 's' : ''}?</p>
            <p className="adm-confirm-text">All NDA files and data for these consultants will be permanently removed. This cannot be undone.</p>
            <div className="adm-confirm-actions">
              <button className="adm-btn adm-btn-ghost adm-btn-sm" onClick={() => setConfirmBulkDel(false)}>Cancel</button>
              <button className="adm-btn adm-btn-danger adm-btn-sm" onClick={() => { setConfirmBulkDel(false); bulkDeleteConsultants(); }}>Delete</button>
            </div>
          </div>
        </div>
      )}

      {/* List */}
      {loading
        ? <div className="adm-empty"><span className="adm-spinner" /></div>
        : filtered.length === 0
          ? <div className="adm-empty">No consultants{filter !== 'all' ? ' with this status' : ''} yet.</div>
          : <div className="ct-list">
              {filtered.map(c => {
                const sm = CNS_STATUS[c.status] || CNS_STATUS.invited;
                const isOpen = expandedId === c.id;
                const link = `${window.location.origin}/onboarding/${c.token}`;
                return (
                  <div key={c.id} className={`ct-row${isOpen ? ' ct-row-open' : ''}${sel.selected.has(c.id) ? ' adm-row-selected' : ''}`}>
                    <div className="ct-row-main" onClick={() => setExpandedId(isOpen ? null : c.id)}>
                      <input type="checkbox" className="adm-checkbox" checked={sel.selected.has(c.id)} onChange={() => sel.toggle(c.id)} onClick={e => e.stopPropagation()} />
                      <div className="ct-row-left">
                        <div className="cns-step-dot" style={{ background: sm.color, boxShadow: `0 0 8px ${sm.color}40` }}>{sm.step}</div>
                        <div className="ct-cns-info">
                          <div className="ct-title">{c.name || <span style={{ color: 'rgba(255,255,255,0.3)', fontStyle: 'italic' }}>Name pending</span>}</div>
                          <div className="ct-cns-meta">
                            <span className="ct-cns-email">{c.email}</span>
                            {c.mobile && <span className="ct-cns-phone">{c.mobile}</span>}
                            {c.nda_number && <span className="ct-cns-nda">{c.nda_number}</span>}
                          </div>
                        </div>
                      </div>
                      <div className="ct-row-right">
                        {c.status === 'invited' && (
                          <button
                            className={`adm-btn adm-btn-sm${copied === c.token ? '' : ' adm-btn-ghost'}`}
                            style={{ fontSize: 14 }}
                            onClick={e => { e.stopPropagation(); copyLink(c.token); }}
                          >
                            {copied === c.token ? '✓ Copied' : 'Copy Link'}
                          </button>
                        )}
                        {c.signed_file && (
                          <button className="adm-btn adm-btn-ghost adm-btn-sm" style={{ fontSize: 13 }}
                            onClick={e => { e.stopPropagation(); downloadFile(c.signed_file); }}>
                            ↓ Signed NDA
                          </button>
                        )}
                        {c.final_file && (
                          <button className="adm-btn adm-btn-sm" style={{ fontSize: 13 }}
                            onClick={e => { e.stopPropagation(); downloadFile(c.final_file); }}>
                            ↓ Final NDA
                          </button>
                        )}
                        {c.nda_number && (
                          <button className="adm-btn adm-btn-ghost adm-btn-sm" style={{ fontSize: 13 }}
                            onClick={e => { e.stopPropagation(); fetchAndOpenNDA(c.id); }}>
                            📄 View NDA
                          </button>
                        )}
                        <span style={{ fontSize: 14, fontWeight: 600, letterSpacing: 2, textTransform: 'uppercase',
                          color: sm.color, background: `${sm.color}1a`, padding: '3px 10px', borderRadius: 99 }}>
                          {sm.label}
                        </span>
                        <span className="ct-ts">{ctFmt(c.invited_at)}</span>
                        <span className="ct-chevron">{isOpen ? '▲' : '▼'}</span>
                      </div>
                    </div>

                    {/* Mini progress strip always visible */}
                    <div className="cns-prog-mini-wrap">
                      <div className="cns-prog-mini-fill" style={{ width: `${[20,40,60,80,100][statusToStepIndex(c.status)]}%`, background: c.status === 'counter_signed' ? '#3bdfb5' : '#f39c12' }} />
                    </div>

                    {isOpen && (
                      <div className="ct-detail">
                        {/* Progress bar */}
                        <CnsProgressBar status={c.status} />

                        {/* Numbered step list */}
                        <div className="cns-steps-list">

                          {/* 0 — Personal Details */}
                          <div className={`cns-sl-row${c.name ? ' cns-sl-done' : ' cns-sl-active'}`}>
                            <div className="cns-sl-num">0</div>
                            <div className="cns-sl-body">
                              <div className="cns-sl-title">Personal Details</div>
                              {c.name ? (
                                <div className="cns-sl-info">
                                  <span className="cns-sl-name">{c.name}</span>
                                  <span className="cns-sl-sep">·</span>
                                  <span>{c.email}</span>
                                  {c.mobile && <><span className="cns-sl-sep">·</span><span>{c.mobile}</span></>}
                                  {c.nda_number && <><span className="cns-sl-sep">·</span><span className="cns-id-nda">{c.nda_number}</span></>}
                                </div>
                              ) : (
                                <div className="cns-sl-sub">{c.email} — details not yet submitted</div>
                              )}
                            </div>
                            <div className="cns-sl-actions">
                              <button className="adm-btn adm-btn-ghost adm-btn-sm" onClick={e => { e.stopPropagation(); setFillModal(c); }}>
                                ✏️ Edit
                              </button>
                            </div>
                          </div>

                          {/* 1 — Email Sent */}
                          <div className="cns-sl-row cns-sl-done">
                            <div className="cns-sl-num">1</div>
                            <div className="cns-sl-body">
                              <div className="cns-sl-title">Email Sent</div>
                              <div className="cns-sl-link">{link}</div>
                              {c.invited_at && <div className="cns-sl-ts">{ctFmt(c.invited_at)}</div>}
                            </div>
                            <div className="cns-sl-actions">
                              <button className="adm-btn adm-btn-ghost adm-btn-sm" onClick={e => { e.stopPropagation(); copyLink(c.token); }}>
                                {copied === c.token ? '✓ Copied' : '🔗 Copy Link'}
                              </button>
                            </div>
                          </div>

                          {/* 2 — NDA */}
                          <div className={`cns-sl-row${c.nda_number ? ' cns-sl-done' : ['submitted','nda_ready'].includes(c.status) ? ' cns-sl-active' : ' cns-sl-locked'}`}>
                            <div className="cns-sl-num">2</div>
                            <div className="cns-sl-body">
                              <div className="cns-sl-title">NDA Pending</div>
                              {c.nda_number
                                ? <div className="cns-sl-info"><span className="cns-id-nda">{c.nda_number}</span>{c.nda_generated_at && <span className="cns-sl-ts">{ctFmt(c.nda_generated_at)}</span>}</div>
                                : <div className="cns-sl-sub">{c.status === 'submitted' ? 'Ready to generate' : 'Awaiting consultant submission'}</div>
                              }
                            </div>
                            <div className="cns-sl-actions">
                              {c.nda_number && (
                                <>
                                  <button className="adm-btn adm-btn-ghost adm-btn-sm" onClick={e => { e.stopPropagation(); fetchAndOpenNDA(c.id); }}>View</button>
                                  <button className="adm-btn adm-btn-ghost adm-btn-sm" onClick={e => { e.stopPropagation(); fetchAndOpenNDA(c.id, true); }}>Download</button>
                                </>
                              )}
                              {c.status === 'submitted' && (
                                <button className="adm-btn adm-btn-sm" onClick={e => { e.stopPropagation(); generateNDA(c); }}>Generate NDA</button>
                              )}
                              {c.status === 'nda_ready' && (
                                <button className="adm-btn adm-btn-ghost adm-btn-sm" onClick={e => { e.stopPropagation(); resendNda(c.id); }}>Resend Email</button>
                              )}
                            </div>
                          </div>

                          {/* 3 — Signed & Uploaded */}
                          <div className={`cns-sl-row${c.signed_file ? ' cns-sl-done' : c.status === 'nda_ready' ? ' cns-sl-active' : ' cns-sl-locked'}`}>
                            <div className="cns-sl-num">3</div>
                            <div className="cns-sl-body">
                              <div className="cns-sl-title">Signed &amp; Uploaded</div>
                              {c.signed_file
                                ? <div className="cns-sl-sub">{c.signed_file.replace(/^(?:cons_\d+_|\d+_)/, '')}</div>
                                : <div className="cns-sl-sub">{c.status === 'nda_ready' ? '⏳ Awaiting consultant upload' : '—'}</div>
                              }
                            </div>
                            <div className="cns-sl-actions">
                              {c.signed_file && (
                                <button className="adm-btn adm-btn-ghost adm-btn-sm" onClick={e => { e.stopPropagation(); downloadFile(c.signed_file); }}>Download</button>
                              )}
                              {c.status === 'nda_ready' && (
                                <CtUploadBtn
                                  label="⬆ Upload Signed"
                                  uploadUrl={`${API}/admin/consultants/${c.id}/upload-signed`}
                                  adminToken={adminToken}
                                  onUpload={() => { load(); showToast('Consultant-signed file uploaded.'); }}
                                />
                              )}
                            </div>
                          </div>

                          {/* 4 — Counter-Signed */}
                          <div className={`cns-sl-row${c.final_file ? ' cns-sl-done' : c.status === 'consultant_signed' ? ' cns-sl-active' : ' cns-sl-locked'}`}>
                            <div className="cns-sl-num">4</div>
                            <div className="cns-sl-body">
                              <div className="cns-sl-title">Counter-Signed</div>
                              {c.final_file
                                ? <div className="cns-sl-sub">{c.final_file.replace(/^(?:cons_\d+_|\d+_)/, '')}</div>
                                : <div className="cns-sl-sub">{c.status === 'consultant_signed' ? '⏳ Awaiting your counter-signature' : '—'}</div>
                              }
                            </div>
                            <div className="cns-sl-actions">
                              {c.final_file && (
                                <button className="adm-btn adm-btn-ghost adm-btn-sm" onClick={e => { e.stopPropagation(); downloadFile(c.final_file); }}>Download</button>
                              )}
                              {c.status === 'consultant_signed' && (
                                <CtUploadBtn
                                  label="⬆ Upload Counter-Signed"
                                  uploadUrl={`${API}/admin/consultants/${c.id}/upload-final`}
                                  adminToken={adminToken}
                                  onUpload={(data) => {
                                    load();
                                    showToast(data?.emailSent
                                      ? `Contract fully executed. Email sent to ${c.email}.`
                                      : 'Contract fully executed. (Email delivery failed.)');
                                  }}
                                />
                              )}
                            </div>
                          </div>

                          {/* 5 — Completed */}
                          <div className={`cns-sl-row${c.status === 'counter_signed' ? ' cns-sl-done' : ' cns-sl-locked'}`}>
                            <div className="cns-sl-num">5</div>
                            <div className="cns-sl-body">
                              <div className="cns-sl-title">Completed</div>
                              {c.counter_signed_at
                                ? <div className="cns-sl-ts">{ctFmt(c.counter_signed_at)}</div>
                                : <div className="cns-sl-sub">—</div>
                              }
                            </div>
                            <div className="cns-sl-actions">
                              {c.final_file && c.status === 'counter_signed' && (
                                <button className="adm-btn adm-btn-ghost adm-btn-sm" onClick={e => { e.stopPropagation(); downloadFile(c.final_file); }}>Download</button>
                              )}
                            </div>
                          </div>

                        </div>

                        {/* Delete */}
                        <div className="ct-detail-actions">
                          <button className="adm-btn adm-btn-danger adm-btn-sm" onClick={() => setConfirmDel(c.id)}>Delete</button>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
      }

      {/* Fill Details modal */}
      {fillModal && (
        <div className="adm-confirm-overlay">
          <div className="ct-modal">
            <div className="ct-modal-title">Fill Consultant Details</div>
            <p style={{ fontSize: 16, color: 'var(--adm-muted)', marginBottom: 20 }}>
              Filling on behalf of <strong style={{ color: '#fff' }}>{fillModal.email}</strong>. This saves their details and marks the record as ready for NDA generation.
            </p>
            <CnsDetailForm
              initial={fillModal.name ? {
                name: fillModal.name, mobile: fillModal.mobile || '', address: fillModal.address || '',
                bankName: fillModal.bank_name || '', bankBranch: fillModal.bank_branch || '', accountHolder: fillModal.account_holder || '',
                iban: fillModal.iban || '', swift: fillModal.swift || '', upiId: fillModal.upi_id || '',
              } : null}
              onSave={fillDetails}
              onCancel={() => setFillModal(null)}
              saving={fillSaving}
            />
          </div>
        </div>
      )}

      {confirmDel && (
        <div className="adm-confirm-overlay">
          <div className="adm-confirm-box">
            <p className="adm-confirm-title">Delete consultant record?</p>
            <p className="adm-confirm-text">This permanently removes all data for this consultant and cannot be undone.</p>
            <div className="adm-confirm-actions">
              <button className="adm-btn adm-btn-ghost adm-btn-sm" onClick={() => setConfirmDel(null)}>Cancel</button>
              <button className="adm-btn adm-btn-danger adm-btn-sm" onClick={() => del(confirmDel)}>Delete</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Organic Shapes: pure utility (outside component) ──
function osDrawPath(ctx, pts, r) {
  if (pts.length < 3) return;
  ctx.beginPath();
  for (let i = 0; i < pts.length; i++) {
    const p1 = pts[i], p2 = pts[(i + 1) % pts.length], p3 = pts[(i + 2) % pts.length];
    const dx1 = p2.x - p1.x, dy1 = p2.y - p1.y;
    const dx2 = p3.x - p2.x, dy2 = p3.y - p2.y;
    const l1 = Math.sqrt(dx1 * dx1 + dy1 * dy1);
    const l2 = Math.sqrt(dx2 * dx2 + dy2 * dy2);
    if (!l1 || !l2) continue;
    const cr = Math.min(r, l1 / 2.1, l2 / 2.1);
    const sx = p2.x - (dx1 / l1) * cr, sy = p2.y - (dy1 / l1) * cr;
    const ex = p2.x + (dx2 / l2) * cr, ey = p2.y + (dy2 / l2) * cr;
    if (i === 0) ctx.moveTo(sx, sy); else ctx.lineTo(sx, sy);
    ctx.quadraticCurveTo(p2.x, p2.y, ex, ey);
  }
  ctx.closePath();
}

function osFileToDataURL(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = e => resolve(e.target.result);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

function osApplyImageToShape(src, shape) {
  return new Promise(resolve => {
    const img = new Image();
    img.onload = () => {
      // Cap at 900px to keep localStorage usage low
      const MAX = 900;
      const scale = Math.min(1, MAX / Math.max(img.width, img.height));
      const w = Math.round(img.width * scale);
      const h = Math.round(img.height * scale);
      const c = document.createElement('canvas');
      c.width = w; c.height = h;
      const cx = c.getContext('2d');
      const pts = shape.points.map(p => ({
        x: (shape.flipH ? 1 - p.x : p.x) * w,
        y: (shape.flipV ? 1 - p.y : p.y) * h,
      }));
      cx.save();
      osDrawPath(cx, pts, shape.smoothing * (w / 800));
      cx.clip();
      cx.drawImage(img, 0, 0, w, h);
      cx.restore();
      // JPEG at 0.82 quality — ~5-10x smaller than PNG
      resolve(c.toDataURL('image/jpeg', 0.82));
    };
    img.src = src;
  });
}

// ── Organic Shapes View ──
function OrganicShapes() {
  const [tab, setTab]           = useState('creator');
  const [points, setPoints]     = useState([
    { x: 0.25, y: 0.2 }, { x: 0.5, y: 0.1 }, { x: 0.75, y: 0.2 },
    { x: 0.85, y: 0.65 }, { x: 0.5, y: 0.9 }, { x: 0.15, y: 0.65 },
  ]);
  const [smoothing, setSmoothing] = useState(160);
  const [flipH, setFlipH]         = useState(false);
  const [flipV, setFlipV]         = useState(false);
  const [color, setColor]         = useState('#4a90d9');
  const [library, setLibrary]     = useState(() => JSON.parse(localStorage.getItem('os_lib')     || '[]'));
  const [gallery, setGallery]     = useState(() => JSON.parse(localStorage.getItem('os_gallery') || '[]'));
  const [libSel, setLibSel]       = useState(new Set());
  const [galSel, setGalSel]       = useState(new Set());
  const [saved, setSaved]         = useState('');
  const [confirmDel, setConfirmDel] = useState(null); // 'lib' | 'gal' | null

  const canvasRef  = useRef(null);
  const wrapRef    = useRef(null);
  const dragRef    = useRef(-1);
  const stateRef   = useRef({ points, smoothing, flipH, flipV, color });

  // keep stateRef in sync for use inside RAF/event handlers
  useEffect(() => { stateRef.current = { points, smoothing, flipH, flipV, color }; }, [points, smoothing, flipH, flipV, color]);

  const drawCanvas = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    const { points: pts, smoothing: sm, flipH: fh, flipV: fv, color: col } = stateRef.current;
    const W = canvas.width, H = canvas.height;

    ctx.clearRect(0, 0, W, H);

    // Subtle grid background
    ctx.fillStyle = '#0b1120';
    ctx.fillRect(0, 0, W, H);
    const gs = 32;
    ctx.strokeStyle = 'rgba(110,252,245,0.04)';
    ctx.lineWidth = 1;
    for (let x = 0; x < W; x += gs) { ctx.beginPath(); ctx.moveTo(x, 0); ctx.lineTo(x, H); ctx.stroke(); }
    for (let y = 0; y < H; y += gs) { ctx.beginPath(); ctx.moveTo(0, y); ctx.lineTo(W, y); ctx.stroke(); }

    // Map normalised points → canvas coords (respecting flip)
    const mapped = pts.map(p => ({
      x: (fh ? 1 - p.x : p.x) * W,
      y: (fv ? 1 - p.y : p.y) * H,
    }));

    // Shape shadow
    ctx.save();
    ctx.shadowColor = 'rgba(0,0,0,0.5)';
    ctx.shadowBlur = 40;
    ctx.shadowOffsetY = 10;
    ctx.fillStyle = col;
    osDrawPath(ctx, mapped, sm);
    ctx.fill();
    ctx.restore();

    // Handle lines
    const cx = W / 2, cy = H / 2;
    mapped.forEach(p => {
      ctx.beginPath();
      ctx.moveTo(cx, cy);
      ctx.lineTo(p.x, p.y);
      ctx.strokeStyle = 'rgba(255,255,255,0.06)';
      ctx.lineWidth = 1;
      ctx.stroke();
    });

    // Handles
    mapped.forEach((p, i) => {
      const active = i === dragRef.current;
      ctx.beginPath();
      ctx.arc(p.x, p.y, active ? 11 : 8, 0, Math.PI * 2);
      ctx.fillStyle = active ? '#fc0071' : 'rgba(252,0,113,0.75)';
      ctx.fill();
      ctx.strokeStyle = '#fff';
      ctx.lineWidth = 2;
      ctx.stroke();
    });
  }, []);

  // ResizeObserver keeps canvas sized to its wrapper
  useEffect(() => {
    const wrap = wrapRef.current;
    const canvas = canvasRef.current;
    if (!wrap || !canvas) return;
    const ro = new ResizeObserver(() => {
      canvas.width  = wrap.clientWidth;
      canvas.height = wrap.clientHeight;
      drawCanvas();
    });
    ro.observe(wrap);
    canvas.width  = wrap.clientWidth;
    canvas.height = wrap.clientHeight;
    return () => ro.disconnect();
  }, [drawCanvas]);

  // Redraw on any state change
  useEffect(() => { drawCanvas(); }, [points, smoothing, flipH, flipV, color, drawCanvas]);

  // ── Mouse / touch drag ──
  const getPt = (e) => {
    const r = canvasRef.current.getBoundingClientRect();
    const raw = e.touches ? e.touches[0] : e;
    return { x: (raw.clientX - r.left) / r.width, y: (raw.clientY - r.top) / r.height };
  };

  const onDown = (e) => {
    const pos = getPt(e);
    const { points: pts, flipH: fh, flipV: fv } = stateRef.current;
    const HIT = 0.03;
    const idx = pts.findIndex(p => {
      const dx = (fh ? 1 - p.x : p.x) - pos.x;
      const dy = (fv ? 1 - p.y : p.y) - pos.y;
      return Math.sqrt(dx * dx + dy * dy) < HIT;
    });
    if (idx !== -1) { dragRef.current = idx; e.preventDefault(); }
  };

  const onMove = (e) => {
    if (dragRef.current === -1) return;
    const pos = getPt(e);
    const { flipH: fh, flipV: fv } = stateRef.current;
    const nx = Math.max(0, Math.min(1, fh ? 1 - pos.x : pos.x));
    const ny = Math.max(0, Math.min(1, fv ? 1 - pos.y : pos.y));
    const next = stateRef.current.points.map((p, i) => i === dragRef.current ? { x: nx, y: ny } : p);
    stateRef.current = { ...stateRef.current, points: next };
    setPoints(next);
  };

  const onUp = () => { dragRef.current = -1; drawCanvas(); };

  // ── Shape preview (for library card) ──
  const makePreview = (pts, sm, fh, fv, col) => {
    const c = document.createElement('canvas'); c.width = 280; c.height = 280;
    const cx = c.getContext('2d');
    cx.fillStyle = '#0b1120'; cx.fillRect(0, 0, 280, 280);
    cx.fillStyle = col;
    osDrawPath(cx, pts.map(p => ({
      x: (fh ? 1 - p.x : p.x) * 280,
      y: (fv ? 1 - p.y : p.y) * 280,
    })), sm * 0.35);
    cx.fill();
    return c.toDataURL('image/jpeg', 0.82);
  };

  // ── Save to library ──
  const saveToLibrary = () => {
    const shape = {
      id: Date.now(), points, smoothing, flipH, flipV, color,
      preview: makePreview(points, smoothing, flipH, flipV, color),
      image: null,
    };
    const next = [...library, shape];
    setLibrary(next);
    localStorage.setItem('os_lib', JSON.stringify(next));
    setSaved('Saved to library!');
    setTimeout(() => setSaved(''), 2500);
  };

  // ── Apply image to selected library shapes ──
  const applyImages = async (files) => {
    if (!library.length) { setSaved('No shapes in library yet.'); setTimeout(() => setSaved(''), 2500); return; }

    // Convert all files to data URLs upfront — avoids stale object URLs in async loops
    const srcs = await Promise.all(files.map(osFileToDataURL));

    const targets = libSel.size > 0
      ? Array.from(libSel)
      : library.map((_, i) => i);

    const next = [...library];
    for (let i = 0; i < srcs.length; i++) {
      const idx = targets[i % targets.length];
      if (next[idx]) next[idx] = { ...next[idx], image: await osApplyImageToShape(srcs[i], next[idx]) };
    }
    setLibrary(next);
    try {
      localStorage.setItem('os_lib', JSON.stringify(next));
      setSaved(`${srcs.length} image(s) applied.`);
    } catch {
      setSaved('Storage full — try deleting unused shapes first.');
    }
    setTimeout(() => setSaved(''), 3000);
  };

  // ── Load shape into editor ──
  const loadShape = (s) => {
    setPoints(s.points); setSmoothing(s.smoothing);
    setFlipH(s.flipH);   setFlipV(s.flipV); setColor(s.color);
    setTab('creator');
  };

  // ── Move to gallery ──
  const toGallery = () => {
    const imgs = Array.from(libSel).map(i => library[i]?.image || library[i]?.preview).filter(Boolean);
    if (!imgs.length) return;
    const next = [...gallery, ...imgs];
    setGallery(next); localStorage.setItem('os_gallery', JSON.stringify(next));
    setSaved(`${imgs.length} picture(s) moved to gallery!`);
    setTimeout(() => setSaved(''), 2500);
  };

  // ── Delete helpers ──
  const delLib = () => {
    const next = library.filter((_, i) => !libSel.has(i));
    setLibrary(next); setLibSel(new Set()); localStorage.setItem('os_lib', JSON.stringify(next));
  };

  const clearImage = (i) => {
    const next = library.map((s, idx) => idx === i ? { ...s, image: null } : s);
    setLibrary(next);
    localStorage.setItem('os_lib', JSON.stringify(next));
  };
  const delGal = () => {
    const next = gallery.filter((_, i) => !galSel.has(i));
    setGallery(next); setGalSel(new Set()); localStorage.setItem('os_gallery', JSON.stringify(next));
  };

  // ── Download gallery ──
  const download = () => {
    let d = 0;
    galSel.forEach(i => {
      setTimeout(() => {
        const a = document.createElement('a'); a.download = `shape-${i + 1}.png`; a.href = gallery[i]; a.click();
      }, d++ * 350);
    });
  };

  // ── Toggle selection helpers ──
  const toggleLib = (i) => setLibSel(prev => { const s = new Set(prev); s.has(i) ? s.delete(i) : s.add(i); return s; });
  const toggleGal = (i) => setGalSel(prev => { const s = new Set(prev); s.has(i) ? s.delete(i) : s.add(i); return s; });

  const autoGenerate = (style) => {
    const cx = 0.5, cy = 0.5;
    let pts = [];
    if (style === 'blob') {
      const n = 6 + Math.floor(Math.random() * 4);
      for (let i = 0; i < n; i++) {
        const angle = (i / n) * Math.PI * 2;
        const r = 0.25 + Math.random() * 0.2;
        pts.push({ x: cx + Math.cos(angle) * r, y: cy + Math.sin(angle) * r });
      }
      setSmoothing(180 + Math.floor(Math.random() * 80));
    } else if (style === 'star') {
      const n = 5 + Math.floor(Math.random() * 3);
      for (let i = 0; i < n * 2; i++) {
        const angle = (i / (n * 2)) * Math.PI * 2 - Math.PI / 2;
        const r = i % 2 === 0 ? 0.32 + Math.random() * 0.1 : 0.12 + Math.random() * 0.08;
        pts.push({ x: cx + Math.cos(angle) * r, y: cy + Math.sin(angle) * r });
      }
      setSmoothing(40 + Math.floor(Math.random() * 60));
    } else if (style === 'random') {
      const n = 5 + Math.floor(Math.random() * 5);
      for (let i = 0; i < n; i++) {
        const angle = (i / n) * Math.PI * 2 + (Math.random() - 0.5) * 0.8;
        const r = 0.15 + Math.random() * 0.3;
        pts.push({ x: Math.max(0.05, Math.min(0.95, cx + Math.cos(angle) * r)), y: Math.max(0.05, Math.min(0.95, cy + Math.sin(angle) * r)) });
      }
      setSmoothing(80 + Math.floor(Math.random() * 120));
    }
    const palette = ['#4a90d9','#6efcf5','#fc0071','#800080','#f39c12','#27ae60','#e74c3c','#8e44ad'];
    setColor(palette[Math.floor(Math.random() * palette.length)]);
    setPoints(pts);
    setFlipH(false); setFlipV(false);
  };

  return (
    <div className="os-root">
      {/* Tabs */}
      <div className="os-tabs">
        {[
          { id: 'creator', label: 'Create Shape' },
          { id: 'library', label: `Saved Shapes${library.length > 0 ? `  (${library.length})` : ''}` },
          { id: 'gallery', label: `Saved Images${gallery.length > 0 ? `  (${gallery.length})` : ''}` },
        ].map(t => (
          <button key={t.id} className={`os-tab${tab === t.id ? ' active' : ''}`} onClick={() => setTab(t.id)}>
            {t.label}
          </button>
        ))}
      </div>

      {saved && <div className="os-toast">{saved}</div>}

      {/* ── Creator ── */}
      {tab === 'creator' && (
        <div className="os-creator">
          <div className="os-canvas-wrap" ref={wrapRef}>
            <canvas ref={canvasRef} className="os-canvas"
              onMouseDown={onDown} onMouseMove={onMove} onMouseUp={onUp} onMouseLeave={onUp}
              onTouchStart={onDown} onTouchMove={onMove} onTouchEnd={onUp} />
          </div>

          <div className="os-panel">
            <div className="os-panel-section">
              <div className="os-panel-label">Smoothing</div>
              <input type="range" min="0" max="400" value={smoothing}
                onChange={e => setSmoothing(+e.target.value)} className="os-range" />
              <div className="os-range-val">{smoothing}</div>
            </div>

            <div className="os-panel-section">
              <div className="os-panel-label">Fill Color</div>
              <div className="os-color-row">
                <input type="color" value={color} onChange={e => setColor(e.target.value)} className="os-color-pick" />
                <span className="os-color-hex">{color}</span>
              </div>
            </div>

            <div className="os-panel-section">
              <div className="os-panel-label">Auto Generate</div>
              <div className="os-btn-grid">
                <button className="adm-btn adm-btn-ghost adm-btn-sm" onClick={() => autoGenerate('blob')}>Blob</button>
                <button className="adm-btn adm-btn-ghost adm-btn-sm" onClick={() => autoGenerate('star')}>Star</button>
                <button className="adm-btn adm-btn-ghost adm-btn-sm" onClick={() => autoGenerate('random')}>Random</button>
                <button className="adm-btn adm-btn-ghost adm-btn-sm" onClick={() => autoGenerate(['blob','star','random'][Math.floor(Math.random()*3)])}>Surprise</button>
              </div>
            </div>

            <div className="os-panel-section">
              <div className="os-panel-label">Transform</div>
              <div className="os-btn-grid">
                <button className={`adm-btn adm-btn-sm${flipH ? '' : ' adm-btn-ghost'}`} onClick={() => setFlipH(v => !v)}>↔ Flip H</button>
                <button className={`adm-btn adm-btn-sm${flipV ? '' : ' adm-btn-ghost'}`} onClick={() => setFlipV(v => !v)}>↕ Flip V</button>
              </div>
            </div>

            <div className="os-panel-section">
              <div className="os-panel-label">Points  <span className="os-pt-count">{points.length}</span></div>
              <div className="os-btn-grid">
                <button className="adm-btn adm-btn-ghost adm-btn-sm"
                  onClick={() => setPoints(p => [...p, { x: 0.4 + Math.random() * 0.2, y: 0.4 + Math.random() * 0.2 }])}>
                  + Add
                </button>
                <button className="adm-btn adm-btn-ghost adm-btn-sm"
                  onClick={() => setPoints(p => p.length > 3 ? p.slice(0, -1) : p)}>
                  − Remove
                </button>
              </div>
              <button className="adm-btn adm-btn-ghost adm-btn-sm os-reset-btn"
                onClick={() => { setPoints([{x:.25,y:.2},{x:.5,y:.1},{x:.75,y:.2},{x:.85,y:.65},{x:.5,y:.9},{x:.15,y:.65}]); setSmoothing(160); setFlipH(false); setFlipV(false); }}>
                Reset Shape
              </button>
            </div>

            <button className="adm-btn os-save-btn" onClick={saveToLibrary}>
              Save to Library
            </button>
          </div>
        </div>
      )}

      {/* ── Library ── */}
      {tab === 'library' && (
        <div className="os-list-view">
          <div className="os-toolbar">
            <button className="adm-btn adm-btn-ghost adm-btn-sm" onClick={() => setLibSel(new Set(library.map((_, i) => i)))}>Select All</button>
            <button className="adm-btn adm-btn-ghost adm-btn-sm" onClick={() => setLibSel(new Set())}>None</button>
            <button className="adm-btn adm-btn-ghost adm-btn-sm" disabled={!libSel.size}
              onClick={() => { const next = library.map((s, i) => libSel.has(i) ? { ...s, image: null } : s); setLibrary(next); localStorage.setItem('os_lib', JSON.stringify(next)); setSaved('Images cleared.'); setTimeout(() => setSaved(''), 2000); }}>
              Clear Images ({libSel.size})
            </button>
            <button className="adm-btn adm-btn-danger adm-btn-sm" onClick={() => setConfirmDel('lib')} disabled={!libSel.size}>Delete ({libSel.size})</button>
            <label className="adm-btn adm-btn-sm os-upload-btn">
              Apply Image{libSel.size > 1 ? 's' : ''}
              <input type="file" accept="image/*" multiple style={{ display: 'none' }}
                onChange={e => e.target.files.length && applyImages(Array.from(e.target.files))} />
            </label>
            <button className="adm-btn adm-btn-sm" onClick={toGallery} disabled={!libSel.size}>→ Gallery</button>
          </div>
          {library.length === 0
            ? <div className="adm-empty">No shapes yet — create one in the editor.</div>
            : <div className="os-grid">
                {library.map((s, i) => (
                  <div key={s.id} className={`os-card${libSel.has(i) ? ' os-selected' : ''}`} onClick={() => toggleLib(i)}>
                    <img src={s.image || s.preview} alt="" className="os-card-img" />
                    {s.image && <div className="os-card-badge">Image applied</div>}
                    <div className="os-card-footer">
                      <span className="os-card-name">Shape {i + 1}</span>
                      <div style={{ display: 'flex', gap: 6 }}>
                        {s.image && (
                          <button className="adm-btn adm-btn-danger adm-btn-sm" onClick={e => { e.stopPropagation(); clearImage(i); }}>✕ Clear</button>
                        )}
                        <button className="adm-btn adm-btn-ghost adm-btn-sm" onClick={e => { e.stopPropagation(); loadShape(s); }}>Edit</button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
          }
        </div>
      )}

      {/* ── Gallery ── */}
      {tab === 'gallery' && (
        <div className="os-list-view">
          <div className="os-toolbar">
            <button className="adm-btn adm-btn-ghost adm-btn-sm" onClick={() => setGalSel(new Set(gallery.map((_, i) => i)))}>All</button>
            <button className="adm-btn adm-btn-ghost adm-btn-sm" onClick={() => setGalSel(new Set())}>None</button>
            <button className="adm-btn adm-btn-danger adm-btn-sm" onClick={() => setConfirmDel('gal')} disabled={!galSel.size}>Delete ({galSel.size})</button>
            <button className="adm-btn adm-btn-sm" onClick={download} disabled={!galSel.size}>⬇ Download PNG ({galSel.size})</button>
          </div>
          {gallery.length === 0
            ? <div className="adm-empty">No pictures yet — apply images to shapes in the Library and save them here.</div>
            : <div className="os-grid">
                {gallery.map((src, i) => (
                  <div key={i} className={`os-card${galSel.has(i) ? ' os-selected' : ''}`} onClick={() => toggleGal(i)}>
                    <img src={src} alt="" className="os-card-img" />
                    <div className="os-card-footer">
                      <span className="os-card-name">Picture {i + 1}</span>
                    </div>
                  </div>
                ))}
              </div>
          }
        </div>
      )}

      {confirmDel && (
        <div className="adm-confirm-overlay">
          <div className="adm-confirm-box">
            <p className="adm-confirm-title">
              {confirmDel === 'lib'
                ? `Delete ${libSel.size} shape${libSel.size !== 1 ? 's' : ''} from Library?`
                : `Delete ${galSel.size} picture${galSel.size !== 1 ? 's' : ''} from Gallery?`}
            </p>
            <p className="adm-confirm-text">This cannot be undone.</p>
            <div className="adm-confirm-actions">
              <button className="adm-btn adm-btn-ghost adm-btn-sm" onClick={() => setConfirmDel(null)}>Cancel</button>
              <button className="adm-btn adm-btn-danger adm-btn-sm" onClick={() => { confirmDel === 'lib' ? delLib() : delGal(); setConfirmDel(null); }}>Delete</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Consultant Autocomplete ──
function ConsultantSearch({ consultants, value, onChange }) {
  const [query, setQuery]   = useState('');
  const [open, setOpen]     = useState(false);
  const [focused, setFocused] = useState(false);
  const wrapRef = useRef(null);

  const selected = consultants.find(c => String(c.id) === String(value));

  const filtered = query.trim()
    ? consultants.filter(c =>
        (c.name || '').toLowerCase().includes(query.toLowerCase()) ||
        (c.email || '').toLowerCase().includes(query.toLowerCase()) ||
        (c.nda_number || '').toLowerCase().includes(query.toLowerCase())
      )
    : consultants;

  useEffect(() => {
    const handler = e => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const pick = (c) => {
    onChange(String(c.id));
    setQuery('');
    setOpen(false);
  };

  const clear = () => { onChange(''); setQuery(''); };

  return (
    <div className="cns-search-wrap" ref={wrapRef}>
      {selected && !focused ? (
        <div className="cns-search-selected">
          <div className="cns-search-selected-inner">
            <span className="cns-search-sel-name">{selected.name || selected.email}</span>
            <span className="cns-search-sel-meta">{selected.email}{selected.nda_number ? ` · ${selected.nda_number}` : ''}</span>
          </div>
          <button className="cns-search-clear" onClick={clear} type="button">✕</button>
        </div>
      ) : (
        <input
          className="adm-input"
          placeholder="Search by name, email or NDA number…"
          value={query}
          onFocus={() => { setFocused(true); setOpen(true); }}
          onBlur={() => { setTimeout(() => setFocused(false), 150); }}
          onChange={e => { setQuery(e.target.value); setOpen(true); }}
          autoComplete="off"
        />
      )}
      {open && !selected && (
        <div className="cns-search-dropdown">
          {filtered.length === 0
            ? <div className="cns-search-empty">No matching consultants</div>
            : filtered.map(c => (
                <div key={c.id} className="cns-search-option" onMouseDown={() => pick(c)}>
                  <span className="cns-search-opt-name">{c.name || c.email}</span>
                  <span className="cns-search-opt-meta">{c.name ? c.email : ''}{c.nda_number ? ` · ${c.nda_number}` : ''}</span>
                </div>
              ))
          }
        </div>
      )}
    </div>
  );
}

// ── Safe-Pay: Payments ──
const PAYMENT_CURRENCIES = ['EUR', 'USD', 'GBP', 'INR', 'AED'];
const PAY_EMPTY = { consultantId: '', amount: '', currency: 'EUR', serviceDate: '', projectName: '', description: '', paymentDate: '' };

function Payments({ token: adminToken }) {
  const [consultants, setConsultants] = useState([]);
  const [receipts, setReceipts]       = useState([]);
  const [form, setForm]               = useState(PAY_EMPTY);
  const [sending, setSending]         = useState(false);
  const [toast, setToast]             = useState('');
  const [showForm, setShowForm]       = useState(false);
  const [pendingEmail, setPendingEmail] = useState(null); // { id, receiptNumber, email }
  const [emailSending, setEmailSending] = useState(false);
  const headers = { 'x-admin-token': adminToken, 'Content-Type': 'application/json' };

  const showToast = (msg) => { setToast(msg); setTimeout(() => setToast(''), 3500); };

  const loadData = useCallback(async () => {
    const [cRes, rRes] = await Promise.all([
      fetch(`${API}/admin/consultants`, { headers: { 'x-admin-token': adminToken } }).then(r => r.json()),
      fetch(`${API}/admin/payments`,    { headers: { 'x-admin-token': adminToken } }).then(r => r.json()),
    ]);
    setConsultants(Array.isArray(cRes) ? cRes.filter(c => c.status === 'counter_signed') : []);
    setReceipts(Array.isArray(rRes) ? rRes : []);
  }, [adminToken]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { loadData(); }, [loadData]);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const selectedConsultant = consultants.find(c => String(c.id) === String(form.consultantId));
  const canSend = selectedConsultant && validAmount(form.amount) && form.serviceDate.trim() && form.projectName.trim();

  const handleSend = async () => {
    if (!canSend) return;
    setSending(true);
    try {
      const res = await fetch(`${API}/admin/payments`, {
        method: 'POST', headers,
        body: JSON.stringify({ ...form, consultantId: Number(form.consultantId) }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed');
      setForm(PAY_EMPTY);
      setShowForm(false);
      loadData();
      setPendingEmail({ id: data.id, receiptNumber: data.receiptNumber, email: selectedConsultant.email });
      // auto-preview the new receipt
      try {
        const pr = await fetch(`${API}/admin/payments/${data.id}/html`, { headers: { 'x-admin-token': adminToken } });
        if (pr.ok) {
          const html = await pr.text();
          const w = window.open('', '_blank');
          if (w) { w.document.write(html); w.document.close(); }
        }
      } catch { /* preview is optional */ }
    } catch (err) {
      showToast(err.message);
    } finally {
      setSending(false);
    }
  };

  const sendReceiptEmail = async () => {
    if (!pendingEmail) return;
    setEmailSending(true);
    try {
      const res = await fetch(`${API}/admin/payments/${pendingEmail.id}/send-email`, { method: 'POST', headers });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed');
      showToast(`Email sent to ${pendingEmail.email}`);
    } catch (err) {
      showToast(err.message);
    } finally {
      setEmailSending(false);
      setPendingEmail(null);
    }
  };

  const openReceiptHtml = async (id) => {
    try {
      const res = await fetch(`${API}/admin/payments/${id}/html`, { headers: { 'x-admin-token': adminToken } });
      if (!res.ok) { showToast('Receipt not available.'); return; }
      const html = await res.text();
      const w = window.open('', '_blank');
      if (!w) { showToast('Popup blocked — please allow popups for this site.'); return; }
      w.document.write(html);
      w.document.close();
    } catch { showToast('Failed to load receipt.'); }
  };

  const downloadSigned = async (filename) => {
    try {
      const res = await fetch(`${API}/admin/uploads/${encodeURIComponent(filename)}`, { headers: { 'x-admin-token': adminToken } });
      if (!res.ok) { showToast('File not found.'); return; }
      const blob = await res.blob();
      const url  = URL.createObjectURL(blob);
      const a    = document.createElement('a');
      a.href = url; a.download = filename.replace(/^rec_\d+_/, '');
      document.body.appendChild(a); a.click();
      document.body.removeChild(a); URL.revokeObjectURL(url);
    } catch { showToast('Download failed.'); }
  };

  const markDocumented = async (id) => {
    await fetch(`${API}/admin/payments/${id}/documented`, { method: 'PATCH', headers });
    loadData();
    showToast('Marked as documented.');
  };

  const STATUS_LABEL = { sent: 'Awaiting Signature', signed: 'Signed — Ready', documented: 'Documented ✓' };
  const STATUS_COLOR = { sent: 'rgba(252,200,0,0.18)', signed: 'rgba(110,252,245,0.12)', documented: 'rgba(80,220,120,0.13)' };
  const STATUS_TEXT  = { sent: '#fcc800', signed: '#6efcf5', documented: '#50dc78' };
  const STATUS_PCT   = { sent: 33, signed: 67, documented: 100 };
  const STATUS_BAR   = { sent: '#fcc800', signed: '#6efcf5', documented: '#50dc78' };
  const sym = (cur) => cur === 'EUR' ? '€' : cur === 'USD' ? '$' : cur === 'GBP' ? '£' : (cur || '€');
  const paySel = useSelection(receipts);

  const openEmailModal = (r) => {
    const consultant = consultants.find(c => c.id === r.consultant_id) ||
      { email: r.consultant_email, name: r.consultant_name };
    setPendingEmail({ id: r.id, receiptNumber: r.receipt_number, email: r.consultant_email || consultant.email });
  };
  const bulkMarkDocumented = async () => {
    await Promise.all([...paySel.selected].map(id => fetch(`${API}/admin/payments/${id}/documented`, { method: 'PATCH', headers })));
    paySel.clear(); loadData();
  };

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 8 }}>
        <div>
          <h2 className="adm-page-title">Payment Receipt</h2>
          <p className="adm-page-sub">Generate and track payment receipts. Only fully verified consultants (NDA counter-signed) can receive payments.</p>
        </div>
        <button className="adm-btn adm-btn-sm" onClick={() => setShowForm(s => !s)}>
          {showForm ? '✕ Cancel' : '+ New Receipt'}
        </button>
      </div>

      {toast && <div className="adm-error" style={{ background: 'rgba(110,252,245,0.06)', borderColor: 'rgba(110,252,245,0.2)', color: 'var(--adm-cyan)', marginBottom: 20 }}>{toast}</div>}

      {showForm && (
        <div className="pay-form-card">
          <div className="pay-form-head">New Payment Receipt</div>
          <div className="pay-form-grid">
            <div className="ct-field ct-field-wide">
              <label className="ct-label">Consultant *</label>
              <ConsultantSearch
                consultants={consultants}
                value={form.consultantId}
                onChange={v => set('consultantId', v)}
              />
              {consultants.length === 0 && (
                <span style={{ fontSize: 15, color: 'var(--adm-pink)' }}>No counter-signed consultants yet. Complete the NDA workflow first.</span>
              )}
            </div>
            <div className="ct-field">
              <label className="ct-label">Amount *</label>
              <input className="adm-input" type="number" min="0.01" step="0.01" placeholder="500.00" value={form.amount} onChange={e => set('amount', e.target.value)}
                style={form.amount && !validAmount(form.amount) ? { borderColor: 'var(--adm-pink)' } : {}} />
              {form.amount && !validAmount(form.amount) && <span style={{ fontSize: 12, color: 'var(--adm-pink)' }}>Must be a positive number</span>}
            </div>
            <div className="ct-field">
              <label className="ct-label">Currency</label>
              <select className="adm-input" value={form.currency} onChange={e => set('currency', e.target.value)}>
                {PAYMENT_CURRENCIES.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            <div className="ct-field">
              <label className="ct-label">Service Period *</label>
              <input className="adm-input" placeholder="e.g. April 2026" value={form.serviceDate} onChange={e => set('serviceDate', e.target.value)} />
            </div>
            <div className="ct-field">
              <label className="ct-label">Payment Date</label>
              <input className="adm-input" type="date" value={form.paymentDate} onChange={e => set('paymentDate', e.target.value)} />
            </div>
            <div className="ct-field ct-field-wide">
              <label className="ct-label">Project Name *</label>
              <input className="adm-input" placeholder="e.g. Helios Project Phase 1" value={form.projectName} onChange={e => set('projectName', e.target.value)} />
            </div>
            <div className="ct-field ct-field-wide">
              <label className="ct-label">Description / Milestone</label>
              <input className="adm-input" placeholder="e.g. Milestone 1 — Backend API implementation" value={form.description} onChange={e => set('description', e.target.value)} />
            </div>
          </div>
          <div className="ct-form-actions">
            <button className="adm-btn adm-btn-ghost adm-btn-sm" onClick={() => { setForm(PAY_EMPTY); setShowForm(false); }}>Cancel</button>
            <button className="adm-btn adm-btn-sm" disabled={!canSend || sending} onClick={handleSend}>
              {sending ? <span className="adm-spinner" /> : null}
              {sending ? 'Sending…' : '📤 Generate & Send Receipt'}
            </button>
          </div>
        </div>
      )}

      {/* Receipts list */}
      <div className="adm-section-header" style={{ marginTop: showForm ? 24 : 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <input type="checkbox" className="adm-checkbox" checked={paySel.allSelected} onChange={paySel.toggleAll} title="Select all" />
          <span className="adm-section-title">Payment Receipts</span>
          <span style={{ fontSize: 15, color: 'var(--adm-muted)' }}>{receipts.length} total</span>
        </div>
      </div>
      {paySel.selected.size > 0 && (
        <div className="adm-bulk-bar" style={{ marginBottom: 12 }}>
          <span className="adm-bulk-count">{paySel.selected.size} selected</span>
          <button className="adm-btn adm-btn-ghost adm-btn-sm" onClick={paySel.clear}>Clear</button>
          <button className="adm-btn adm-btn-sm" style={{ fontSize: 14 }} onClick={bulkMarkDocumented}>✓ Mark Documented ({paySel.selected.size})</button>
        </div>
      )}

      {receipts.length === 0 ? (
        <div className="adm-empty">No receipts yet. Create one above.</div>
      ) : (
        <div className="pay-receipt-list">
          {receipts.map(r => (
            <div key={r.id} className={`pay-receipt-card${paySel.selected.has(r.id) ? ' adm-row-selected' : ''}`}>
              <div className="pay-receipt-top">
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <input type="checkbox" className="adm-checkbox" checked={paySel.selected.has(r.id)} onChange={() => paySel.toggle(r.id)} onClick={e => e.stopPropagation()} />
                  <div className="pay-receipt-meta">
                    <span className="pay-receipt-num">{r.receipt_number}</span>
                    <span className="pay-receipt-consultant">{r.consultant_name || r.consultant_email}</span>
                  </div>
                </div>
                <span
                  className="pay-status-badge"
                  style={{ background: STATUS_COLOR[r.status], color: STATUS_TEXT[r.status] }}
                >
                  {STATUS_LABEL[r.status]}
                </span>
              </div>

              {/* Status progress strip */}
              <div className="pay-prog-mini-wrap">
                <div className="pay-prog-mini-fill" style={{ width: `${STATUS_PCT[r.status] || 0}%`, background: STATUS_BAR[r.status] }} />
              </div>

              <div className="pay-receipt-details">
                <span className="pay-receipt-amount">{sym(r.currency)}{Number(r.amount).toFixed(2)} {r.currency}</span>
                <span className="pay-receipt-project">{r.project_name}</span>
                <span className="pay-receipt-date">{r.service_date}</span>
                <span style={{ fontSize: 14, color: 'var(--adm-muted)' }}>NDA: {r.nda_number}</span>
              </div>
              <div className="pay-receipt-actions">
                <button className="adm-btn adm-btn-ghost adm-btn-sm" style={{ fontSize: 14 }} onClick={() => openReceiptHtml(r.id)}>📄 View Receipt</button>
                <button className="adm-btn adm-btn-ghost adm-btn-sm" style={{ fontSize: 14 }} onClick={() => openEmailModal(r)}>📧 Email Consultant</button>
                {r.signed_file ? (
                  <button className="adm-btn adm-btn-ghost adm-btn-sm" style={{ fontSize: 14 }} onClick={() => downloadSigned(r.signed_file)}>↓ Download Signed</button>
                ) : (
                  <CtUploadBtn
                    label="⬆ Upload Signed"
                    uploadUrl={`${API}/admin/payments/${r.id}/upload-signed`}
                    adminToken={adminToken}
                    onUpload={() => { loadData(); showToast('Signed receipt uploaded.'); }}
                  />
                )}
                {r.status === 'signed' && (
                  <button className="adm-btn adm-btn-sm" style={{ fontSize: 14 }} onClick={() => markDocumented(r.id)}>✓ Mark Documented</button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Send-email confirmation modal */}
      {pendingEmail && (
        <div className="adm-confirm-overlay">
          <div className="ct-modal" style={{ maxWidth: 460 }}>
            <div className="ct-modal-title">Send Receipt to Consultant</div>
            <p style={{ fontSize: 16, color: 'var(--adm-muted)', margin: '12px 0 6px', lineHeight: 1.6 }}>
              Receipt <strong style={{ color: 'var(--adm-text)' }}>{pendingEmail.receiptNumber}</strong>
            </p>
            <p style={{ fontSize: 16, color: 'var(--adm-muted)', margin: '0 0 10px', lineHeight: 1.6 }}>
              An email will be sent to <strong style={{ color: 'var(--adm-cyan)' }}>{pendingEmail.email}</strong> with:
            </p>
            <ul style={{ fontSize: 15, color: 'var(--adm-muted)', margin: '0 0 20px', paddingLeft: 20, lineHeight: 2 }}>
              <li>A link to view and print the receipt PDF</li>
              <li>Instructions to sign and upload it back</li>
            </ul>
            <div style={{ display: 'flex', gap: 10, justifyContent: 'space-between', alignItems: 'center' }}>
              <button className="adm-btn adm-btn-ghost adm-btn-sm" onClick={() => openReceiptHtml(pendingEmail.id)}>
                👁 Preview First
              </button>
              <div style={{ display: 'flex', gap: 10 }}>
                <button className="adm-btn adm-btn-ghost adm-btn-sm" onClick={() => setPendingEmail(null)}>Cancel</button>
                <button className="adm-btn adm-btn-sm" disabled={emailSending} onClick={sendReceiptEmail}>
                  {emailSending ? <><span className="adm-spinner" style={{ width: 12, height: 12, borderWidth: 2 }} /> Sending…</> : '📧 Send Email'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Shell (authenticated layout) ──
function Shell({ token, onLogout, onTokenChange, theme, toggleTheme }) {
  const [view, setView] = useState('dashboard');
  const [unread, setUnread] = useState(0);
  const [pendingReceipts, setPendingReceipts] = useState(0);

  useEffect(() => {
    const fetchStats = () => {
      fetch(`${API}/admin/stats`, { headers: { 'x-admin-token': token } })
        .then(r => r.json())
        .then(s => {
          setUnread(s.unread || 0);
          setPendingReceipts(s.pendingReceipts || 0);
        })
        .catch(() => {});
    };
    fetchStats();
    const timer = setInterval(fetchStats, 30000);
    return () => clearInterval(timer);
  }, [token]);

  const adminItems = [
    { id: 'dashboard',   icon: '⬡', label: 'Dashboard' },
    { id: 'inbox',       icon: '✉', label: 'Inbox', badge: unread },
    { id: 'consultants', icon: '◉', label: 'IT Consultants' },
    { id: 'payments',    icon: '💳', label: 'Payment Receipt', badge: pendingReceipts },
  ];

  const contractItems = [
    { id: 'client_details', icon: '👤', label: 'Client Details' },
    { id: 'contracts_msa',  icon: '◫', label: 'MSA' },
    { id: 'contracts_sow',  icon: '◧', label: 'SOW' },
  ];


  const toolItems = [
    { id: 'tools',      icon: '⚙', label: 'Tools' },
    { id: 'qr',         icon: '⬛', label: 'QR Code' },
    { id: 'shapes',     icon: '◈', label: 'Organic Shapes' },
  ];
  const ytItems = [
    { id: 'yt_single',  icon: '▶', label: 'One Link' },
    { id: 'yt_series',  icon: '▶▶', label: 'Series' },
  ];
  const clockItems = [
    { id: 'clock_hours', icon: '⏱', label: 'Clock Hours' },
    { id: 'hours_log',   icon: '📋', label: 'Hours Log' },
  ];

  const renderNavItem = (item, sub = false) => (
    <div
      key={item.id}
      className={`adm-nav-item${view === item.id ? ' active' : ''}${sub ? ' adm-nav-sub' : ''}`}
      onClick={() => setView(item.id)}
    >
      <span className="adm-nav-icon">{item.icon}</span>
      <span>{item.label}</span>
      {item.badge > 0 && <span className="adm-unread-badge">{item.badge}</span>}
    </div>
  );

  return (
    <div className={`adm-root${theme === 'light' ? ' adm-light' : ''}`}>
      <div className="adm-shell">
        <aside className="adm-sidebar">
          <img src={logo} alt="7 Elemente" className="adm-sidebar-logo" />

          <div className="adm-nav-section-label">Administration</div>
          {adminItems.map(i => renderNavItem(i))}

          <div className="adm-nav-section-label" style={{ marginTop: 12 }}>Client Contracts</div>
          {contractItems.map(i => renderNavItem(i, true))}

          <div className="adm-nav-section-label" style={{ marginTop: 12 }}>Tools</div>
          {toolItems.map(i => renderNavItem(i))}

          <div className="adm-nav-section-label" style={{ marginTop: 12 }}>YouTube</div>
          {ytItems.map(i => renderNavItem(i, true))}

          <div className="adm-nav-section-label" style={{ marginTop: 12 }}>Time</div>
          {clockItems.map(i => renderNavItem(i))}

          <div className="adm-sidebar-spacer" />

          <button className="adm-theme-toggle" onClick={toggleTheme} title={theme === 'light' ? 'Switch to Night mode' : 'Switch to Day mode'}>
            <span>{theme === 'light' ? '🌙' : '☀️'}</span>
            <span>{theme === 'light' ? 'Night' : 'Day'}</span>
          </button>

          <div className="adm-sidebar-logout" onClick={onLogout}>
            <span>⎋</span>
            <span>Logout</span>
          </div>
        </aside>

        <main className="adm-main">
          {view === 'dashboard'      && <Dashboard token={token} onNavigate={setView} />}
          {view === 'inbox'          && <Inbox token={token} />}
          {view === 'consultants'    && <Consultants token={token} />}
          {view === 'payments'       && <Payments token={token} />}
          {view === 'client_details' && <ClientDetails token={token} />}
          {view === 'contracts_msa'  && <ContractsMSA token={token} />}
          {view === 'contracts_sow'  && <ContractsSOW token={token} />}
          {view === 'tools'          && <Tools token={token} onTokenChange={onTokenChange} />}
          {view === 'qr'             && <QRGenerator />}
          {view === 'shapes'         && <OrganicShapes />}
          {view === 'yt_single'      && <YouTubeSingle token={token} />}
          {view === 'yt_series'      && <YouTubeSeries token={token} />}
          {view === 'clock_hours'    && <ClockHours token={token} />}
          {view === 'hours_log'      && <HoursLog token={token} />}
        </main>
      </div>
    </div>
  );
}

// ── Root ──
function Admin() {
  const [token, setToken] = useState(() => sessionStorage.getItem('adm_token') || '');
  const [theme, setTheme] = useState(() => localStorage.getItem('adm_theme') || 'dark');

  const handleLogin = (t) => {
    sessionStorage.setItem('adm_token', t);
    setToken(t);
  };

  const handleLogout = () => {
    sessionStorage.removeItem('adm_token');
    setToken('');
  };

  const toggleTheme = () => {
    const next = theme === 'dark' ? 'light' : 'dark';
    setTheme(next);
    localStorage.setItem('adm_theme', next);
  };

  const handleTokenChange = (t) => { sessionStorage.setItem('adm_token', t); setToken(t); };

  if (!token) return <Login onLogin={handleLogin} />;
  return <Shell token={token} onLogout={handleLogout} onTokenChange={handleTokenChange} theme={theme} toggleTheme={toggleTheme} />;
}

export default Admin;
